import * as THREE from 'three';
import { MeshStandardNodeMaterial } from 'three/webgpu';
import {
  attribute,
  bumpMap,
  cameraPosition,
  normalMap,
  normalView,
  normalWorld,
  positionView,
  positionWorld,
  texture,
  uniform,
  uv,
  vec2,
  vec4,
  vertexColor,
  wgslFn,
} from 'three/tsl';
import type { Track } from './TrackLoader';
import { buildCheckpoints } from './Checkpoints';
import { taauMipBiasUniform } from '../render/TaauMipBias';
import type { SurfaceTextureSet } from '../render/SurfaceTextures';
import {
  DEFAULT_STYLE_CONFIG,
  type StyleConfig,
} from '../render/StyleConfig';
import type { BakedSurfaceAssets, BakedPbrSurface } from '../render/BakedSurfaceAssets';
import { installAerialPerspective } from '../render/AerialPerspective';
import { installValueComposition, tagValueBucket } from '../render/ValueComposition';
import type { RendererMode } from '../render/Renderer';
import {
  TRACK_SURFACE_CLEARANCE,
  trackSurfaceHeightAtPosition,
  trackSurfaceHeightAtSample,
} from './TrackElevation';

/**
 * Vertical thickness of the visible slab — the gap between the banked track
 * surface (at `trackSurfaceHeightAtSample(...) + TRACK_SURFACE_CLEARANCE`)
 * and the surrounding flat corridor terrain (at `trackSurface... + TERRAIN_BASE_Y`).
 * Kept in sync with TERRAIN_BASE_Y in Terrain.ts.
 */
const SLAB_BOTTOM_DROP = 0.168;
const SURFACE_LATERAL_SEGMENTS = 8;
const SURFACE_VERTICES_PER_RING = SURFACE_LATERAL_SEGMENTS + 1;
const SURFACE_LONGITUDINAL_SUBDIVISIONS = 4;
const KERB_LONGITUDINAL_SUBDIVISIONS = 4;
const makeNodeUniform = uniform as any;
const makeNodeTexture = texture as any;
const makeNodeAttribute = attribute as any;
const makeNodeBumpMap = bumpMap as any;
void makeNodeBumpMap;
const makeNodeNormalMap = normalMap as any;
void makeNodeNormalMap;
const makeNodeUv = uv as any;
const makeNodeVec2 = vec2 as any;
const makeNodeVec4 = vec4 as any;
const makeNodeVertexColor = vertexColor as any;
const makeWgslFn = wgslFn as any;

export interface TrackMeshes {
  surface: THREE.Mesh;
  sides: THREE.Mesh;
  edgeLines: THREE.Group;
  kerbs: THREE.Group;
  centerlineGuide: THREE.Line;
  kerbSurface: KerbSurface;
  sectorDecals: THREE.Group;
  scrapeDecals: THREE.Group;
  crackDecals: THREE.Group;
  crackRelief: THREE.Group;
  applyStyle(style: StyleConfig): void;
}

export interface BuildTrackMeshesOptions {
  asphaltTextures?: SurfaceTextureSet;
  style?: StyleConfig;
  asphaltShaderMode?: AsphaltShaderMode;
  asphaltLiveDetail?: boolean;
  bakedCracks?: BakedCrackDecals | null;
  bakedSurfaceAssets?: BakedSurfaceAssets | null;
  rendererMode?: RendererMode;
}

export type AsphaltShaderMode = 'fast' | 'detailed' | 'bakedPbr';

export interface BakedCrackDecal {
  name: string;
  positions: number[];
  uv: number[];
  opacity: number;
  renderOrder?: number;
  userData?: Record<string, unknown>;
}

export interface BakedCrackDecals {
  version: 1;
  atlasUrl: string;
  decals: BakedCrackDecal[];
}

export interface KerbSurface {
  readonly bandCount: number;
  isOnKerb(x: number, z: number): boolean;
  /**
   * Height in meters above the track surface contributed by raised kerb
   * geometry at this position. Returns 0 when off the kerb. Smoothly ramps
   * up over the kerb's inner chamfer and tapers to 0 at segment ends so the
   * racer rides up onto and off the kerb without a vertical step.
   */
  heightAt(x: number, z: number): number;
}

const KERB_TURN_WINDOW_POINTS = 8;
const KERB_MIN_WINDOW_HEADING_CHANGE = THREE.MathUtils.degToRad(11);
const KERB_MIN_SEGMENT_ARC = 18;
const KERB_MERGE_GAP_POINTS = 4;
const KERB_SEGMENT_TRIM_POINTS = 2;
const EDGE_LINE_ASPHALT_SHOULDER = 0.78;
const ASPHALT_MAX_CRACK_SEGMENTS = 72;

interface AsphaltShaderUniforms {
  uAsphaltBase: { value: THREE.Color };
  uAggregateColor: { value: THREE.Color };
  uAggregateIntensity: { value: number };
  uGrainTileMeters: { value: number };
  uGrainDetileMix: { value: number };
  uEdgeLineColor: { value: THREE.Color };
  uEdgeLineHalfWidth: { value: number };
  uEdgeLineShoulderMeters: { value: number };
  uEdgeLineOpacity: { value: number };
  uRubberColor: { value: THREE.Color };
  uRubberIntensity: { value: number };
  uRubberLongScale: { value: number };
  uRubberLateralFreq: { value: number };
  uRubberLaneWidth: { value: number };
  uRubberThreshold: { value: number };
  uOilColor: { value: THREE.Color };
  uOilIntensity: { value: number };
  uOilLongScale: { value: number };
  uOilThreshold: { value: number };
  uCrackColor: { value: THREE.Color };
  uCrackIntensity: { value: number };
  uCrackFineChecking: { value: number };
  uCrackDepth: { value: number };
  uCrackSegmentCount: { value: number };
  uCrackSegments: { value: THREE.Vector4[] };
  uCrackParams: { value: THREE.Vector4[] };
  uTrackGlintColor: { value: THREE.Color };
  uTrackGlintIntensity: { value: number };
  uTrackGlintExponent: { value: number };
  uTrackGlintStreakScale: { value: number };
  uTrackGlintBroadness: { value: number };
  uTrackGlintTextureInfluence: { value: number };
  uTrackBumpStrength: { value: number };
  uTrackRoughnessContrast: { value: number };
  uTrackGlintSunDir: { value: THREE.Vector3 };
}

interface BakedAsphaltUniforms {
  uBakedMaterialMap: { value: THREE.Texture };
  uBakedMaterialVScale: { value: number };
  uAsphaltBase: { value: THREE.Color };
  uAggregateColor: { value: THREE.Color };
  uAggregateIntensity: { value: number };
  uGrainTileMeters: { value: number };
  uGrainDetileMix: { value: number };
  uEdgeLineColor: { value: THREE.Color };
  uEdgeLineHalfWidth: { value: number };
  uEdgeLineShoulderMeters: { value: number };
  uEdgeLineOpacity: { value: number };
  uRubberColor: { value: THREE.Color };
  uRubberIntensity: { value: number };
  uRubberLaneWidth: { value: number };
  uOilColor: { value: THREE.Color };
  uOilIntensity: { value: number };
  uTrackGlintColor: { value: THREE.Color };
  uTrackGlintIntensity: { value: number };
  uTrackGlintExponent: { value: number };
  uTrackGlintStreakScale: { value: number };
  uTrackGlintBroadness: { value: number };
  uTrackGlintTextureInfluence: { value: number };
  uTrackBumpStrength: { value: number };
  uTrackRoughnessContrast: { value: number };
  uTrackGlintSunDir: { value: THREE.Vector3 };
}

interface WebGPUAsphaltUniforms {
  gritTexture: any;
  asphaltBase: { value: THREE.Vector3 };
  aggregateColor: { value: THREE.Vector3 };
  aggregateIntensity: { value: number };
  grainTileMeters: { value: number };
  grainDetileMix: { value: number };
  edgeLineColor: { value: THREE.Vector3 };
  edgeLineHalfWidth: { value: number };
  edgeLineShoulderMeters: { value: number };
  edgeLineOpacity: { value: number };
  rubberColor: { value: THREE.Vector3 };
  rubberIntensity: { value: number };
  rubberLongScale: { value: number };
  rubberLateralFreq: { value: number };
  rubberLaneWidth: { value: number };
  rubberThreshold: { value: number };
  oilColor: { value: THREE.Vector3 };
  oilIntensity: { value: number };
  oilLongScale: { value: number };
  oilThreshold: { value: number };
  trackGlintColor: { value: THREE.Vector3 };
  trackGlintIntensity: { value: number };
  trackGlintExponent: { value: number };
  trackGlintStreakScale: { value: number };
  trackGlintBroadness: { value: number };
  trackGlintTextureInfluence: { value: number };
  trackBumpScale: { value: number };
  trackRoughnessContrast: { value: number };
  trackGlintSunDir: { value: THREE.Vector3 };
  baseRoughness: { value: number };
}

export function buildTrackMeshes(track: Track, options: BuildTrackMeshesOptions = {}): TrackMeshes {
  void options.asphaltTextures;
  const style = options.style ?? DEFAULT_STYLE_CONFIG;
  const rendererMode = options.rendererMode ?? 'webgl2';
  const surface = buildSurface(
    track,
    style,
    options.asphaltShaderMode ?? 'detailed',
    options.bakedSurfaceAssets?.asphalt ?? null,
    options.asphaltLiveDetail ?? true,
    rendererMode,
  );
  const sides = buildSurfaceSides(track, rendererMode);
  const edgeLines = buildIntegratedEdgeLineGroup();
  const kerbSurface = createKerbSurface(track, style);
  const kerbs = buildKerbs(track, kerbSurface.segments, rendererMode);
  const centerlineGuide = buildCenterlineGuide(track);
  const sectorDecals = buildSectorDecals(track, rendererMode);
  const scrapeDecals = buildScrapeDecals(track, style, rendererMode);
  const crackRelief = buildCrackRelief(track, style);
  const crackDecals = buildCrackDecals(track, style, options.bakedCracks ?? null, rendererMode);
  tagValueBucket(edgeLines, 'mg');
  tagValueBucket(kerbs, 'mg');
  tagValueBucket(sectorDecals, 'mg');
  tagValueBucket(scrapeDecals, 'mg');
  tagValueBucket(crackRelief, 'mg');
  tagValueBucket(crackDecals, 'mg');
  const meshes: TrackMeshes = {
    surface,
    sides,
    edgeLines,
    kerbs,
    centerlineGuide,
    kerbSurface,
    sectorDecals,
    scrapeDecals,
    crackDecals,
    crackRelief,
    applyStyle(nextStyle) {
      applyAsphaltShaderStyle(surface, track, nextStyle);
      applySurfaceWearStyle(surface, track, nextStyle);
      rebuildKerbs(kerbs, kerbSurface as InternalKerbSurface, track, nextStyle, rendererMode);
      rebuildCrackRelief(crackRelief, track, nextStyle);
      rebuildCrackDecals(crackDecals, track, nextStyle, options.bakedCracks ?? null, rendererMode);
    },
  };
  return meshes;
}

function buildSurface(
  track: Track,
  style: StyleConfig,
  shaderMode: AsphaltShaderMode,
  bakedSurface: BakedPbrSurface | null,
  asphaltLiveDetail: boolean,
  rendererMode: RendererMode,
): THREE.Mesh {
  const n = track.centerline.length;
  const rows = n * SURFACE_LONGITUDINAL_SUBDIVISIONS;
  const cols = SURFACE_VERTICES_PER_RING;
  // Snap the V-axis cycle to an integer number of repeats over the full lap so
  // the closing ring's V (= cycles) and ring 0's V (= 0) are the same texel
  // under RepeatWrapping — without this, the last quad's UV smears the noise
  // across the whole loop and reads as a hard seam at start/finish.
  const totalArc = track.totalLength;
  const cycles = Math.max(1, Math.round(totalArc / 6));
  const meterPerCycle = totalArc / cycles;
  // One extra ring duplicates ring 0's XYZ at V = cycles, so we can drop the
  // (i + 1) % rows wrap and let the seam quad interpolate cleanly.
  const ringsStored = rows + 1;
  const positions = new Float32Array(ringsStored * cols * 3);
  const uvs = new Float32Array(ringsStored * cols * 2);
  const colors = new Float32Array(ringsStored * cols * 3);
  // Per-vertex racing-line U coordinate (centerline-relative, 0..1). The
  // shader's rubber/oil streak masks fade away from this point, so streaks
  // follow the racing line through corners instead of locking to UV center.
  const racingLineUs = new Float32Array(ringsStored * cols);
  // Half the local asphalt width in metres. Edge paint is authored in metres
  // from each side, so the shader needs this to convert into lateral UV.
  const trackHalfWidths = new Float32Array(ringsStored * cols);

  for (let i = 0; i < ringsStored; i++) {
    const isClosingRing = i === rows;
    const sampleIndex = isClosingRing ? 0 : i / SURFACE_LONGITUDINAL_SUBDIVISIONS;
    const sample = sampleRenderRibbon(track, sampleIndex);
    const racingLineU = racingLineCenterU(track, sampleIndex);
    // Closing ring carries the full lap arc so V hits the integer `cycles`
    // boundary; under RepeatWrapping this is congruent to V=0 at ring 0 and
    // the seam quad samples the noise without a discontinuity.
    const arc = isClosingRing ? totalArc : sample.arc;
    const s = arc / meterPerCycle;
    for (let j = 0; j < cols; j++) {
      const u = j / (cols - 1);
      const lateral = THREE.MathUtils.lerp(sample.widthLeft, -sample.widthRight, u);
      const index3 = (i * cols + j) * 3;
      const index2 = (i * cols + j) * 2;
      positions[index3 + 0] = sample.x + sample.nx * lateral;
      positions[index3 + 1] = trackSurfaceHeightAtRenderSample(track, sampleIndex, lateral) + TRACK_SURFACE_CLEARANCE;
      positions[index3 + 2] = sample.z + sample.nz * lateral;
      uvs[index2 + 0] = u;
      uvs[index2 + 1] = s;
      racingLineUs[i * cols + j] = racingLineU;
      trackHalfWidths[i * cols + j] = (sample.widthLeft + sample.widthRight) * 0.5;
    }
  }

  const indices: number[] = [];
  for (let i = 0; i < rows; i++) {
    const j = i + 1;
    for (let k = 0; k < cols - 1; k++) {
      const a0 = i * cols + k;
      const a1 = i * cols + k + 1;
      const b0 = j * cols + k;
      const b1 = j * cols + k + 1;
      indices.push(a0, b1, a1, a0, b0, b1);
    }
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geom.setAttribute('aRacingLineU', new THREE.BufferAttribute(racingLineUs, 1));
  geom.setAttribute('aTrackHalfWidth', new THREE.BufferAttribute(trackHalfWidths, 1));
  geom.setIndex(indices);
  // renderRings is the count of unique centerline-mapped rings (excluding the
  // closing duplicate). Tests treat this as `n × SURFACE_LONGITUDINAL_SUBDIVISIONS`.
  geom.userData.renderRings = rows;
  geom.userData.renderRingsTotal = ringsStored;
  geom.userData.renderColumns = cols;
  geom.userData.grainCycleMeters = meterPerCycle;
  geom.userData.fullLapTextureCycles = cycles;
  writeSurfaceWearColors(geom, track, style);
  geom.computeVertexNormals();

  // The DataTexture only carries grayscale grit (sub-pixel aggregate density).
  // Base color, rubber streaks, oil sheen, and sun glint all live in the
  // shader so they can be tuned independently and follow the racing line.
  const mat = rendererMode === 'webgpu'
    ? (shaderMode === 'bakedPbr' && bakedSurface
      ? makeWebGPUBakedAsphaltNodeMaterial(style, bakedSurface, cycles, asphaltLiveDetail)
      : makeWebGPUAsphaltNodeMaterial(style))
    : new THREE.MeshStandardMaterial({
      color: 0xffffff,
      map: makeProceduralTexture(style),
      side: THREE.DoubleSide,
      vertexColors: true,
      roughness: style.asphalt.baseRoughness,
      metalness: 0,
      envMapIntensity: style.asphalt.baseEnvIntensity,
      toneMapped: true,
    });
  mat.userData.asphaltShaderMode = shaderMode;
  if (rendererMode === 'webgpu') {
    mat.userData.webgpuAsphaltMaterial = true;
    mat.fog = true;
  } else if (shaderMode === 'detailed') installAsphaltShader(mat as THREE.MeshStandardMaterial, track, style);
  else if (shaderMode === 'fast') installFastAsphaltShader(mat as THREE.MeshStandardMaterial, style);
  else if (bakedSurface) {
    applyBakedPbrMaterialMaps(mat as THREE.MeshStandardMaterial, bakedSurface, cycles, style, asphaltLiveDetail);
  }
  if (rendererMode !== 'webgpu') {
    // Aerial perspective on WebGPU is owned by `scene.fogNode` (installed
    // in SceneStyleRig), so we don't need a per-material installer there.
    installAerialPerspective(mat, rendererMode);
  }
  // Value-composition exposure now has a WebGPU node-material code path,
  // so call it for both renderers — without this, mg buckets render at
  // full exposure on WebGPU and the foreground/midground separation washes
  // out vs WebGL.
  installValueComposition(mat, 'mg', rendererMode);
  const mesh = new THREE.Mesh(geom, mat);
  mesh.receiveShadow = true;
  mesh.name = 'track-asphalt-surface';
  tagValueBucket(mesh, 'mg');
  mesh.userData.asphaltStyle = { ...style.asphalt };
  mesh.userData.trackGlintStyle = { ...style.trackGlint };
  return mesh;
}

function applyBakedPbrMaterialMaps(
  material: THREE.MeshStandardMaterial,
  surface: BakedPbrSurface,
  lapCycles: number,
  style: StyleConfig,
  liveDetail: boolean,
): void {
  if (surface.normalUrl) {
    material.normalMap = loadBakedPbrTexture(surface.normalUrl, lapCycles, false);
    const scale = surface.normalScale ?? 1;
    material.normalScale = new THREE.Vector2(scale, scale);
  }
  if (surface.materialUrl) {
    const materialMap = loadBakedPbrTexture(surface.materialUrl, lapCycles, false);
    material.roughnessMap = null;
    installBakedAsphaltShader(material, style, materialMap, lapCycles, { liveDetail });
  } else if (surface.roughnessUrl) {
    material.roughnessMap = loadBakedPbrTexture(surface.roughnessUrl, lapCycles, false);
  }
  material.userData.bakedPbrSurface = surface;
}

function loadBakedPbrTexture(url: string, lapCycles: number, srgb = true): THREE.Texture {
  const texture = new THREE.TextureLoader().load(url);
  texture.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(1, 1 / Math.max(1, lapCycles));
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  // 16 = hardware ceiling on the platforms we ship to. The track ribbon
  // stretches to the horizon along V; anisotropic filtering is what keeps
  // the rubber/oil streak modulation from collapsing into stripes at the
  // vanishing point.
  texture.anisotropy = 16;
  return texture;
}

function loadBakedPbrNodeSampleTexture(url: string, srgb = true): THREE.Texture {
  const texture = new THREE.TextureLoader().load(url);
  texture.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.repeat.set(1, 1);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.anisotropy = 16;
  return texture;
}

const asphaltHashNode = makeWgslFn(`
fn asphalt_hash(p: vec2<f32>) -> f32 {
  var q = fract(p * vec2<f32>(123.34, 456.21));
  q = q + dot(q, q + vec2<f32>(45.32, 45.32));
  return fract(q.x * q.y);
}
`);

const asphaltNoiseNode = makeWgslFn(`
fn asphalt_noise(p: vec2<f32>) -> f32 {
  let i = floor(p);
  let f0 = fract(p);
  let f = f0 * f0 * (vec2<f32>(3.0, 3.0) - 2.0 * f0);
  let a = asphalt_hash(i);
  let b = asphalt_hash(i + vec2<f32>(1.0, 0.0));
  let c = asphalt_hash(i + vec2<f32>(0.0, 1.0));
  let d = asphalt_hash(i + vec2<f32>(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
`, [asphaltHashNode]);

const asphaltFbmNode = makeWgslFn(`
fn asphalt_fbm(p: vec2<f32>) -> f32 {
  var q = p;
  var v = 0.0;
  var a = 0.5;
  for (var i = 0; i < 3; i = i + 1) {
    v = v + a * asphalt_noise(q);
    q = q * 2.03;
    a = a * 0.5;
  }
  return v;
}
`, [asphaltNoiseNode]);

const asphaltColorNode = makeWgslFn(`
fn asphalt_color(
  trackUv: vec2<f32>,
  racingLineU: f32,
  halfWidthMeters: f32,
  gritPrimary: f32,
  gritSecondary: f32,
  asphaltBase: vec3<f32>,
  aggregateColor: vec3<f32>,
  aggregateIntensity: f32,
  grainDetileMix: f32,
  edgeLineColor: vec3<f32>,
  edgeLineHalfWidth: f32,
  edgeLineShoulderMeters: f32,
  edgeLineOpacity: f32,
  rubberColor: vec3<f32>,
  rubberIntensity: f32,
  rubberLongScale: f32,
  rubberLateralFreq: f32,
  rubberLaneWidth: f32,
  rubberThreshold: f32,
  oilColor: vec3<f32>,
  oilIntensity: f32,
  oilLongScale: f32,
  oilThreshold: f32
) -> vec3<f32> {
  let grain = ((gritPrimary - 0.5) + (gritSecondary - 0.5) * grainDetileMix)
    * 2.0 / max(0.001, 1.0 + grainDetileMix);
  var surface = mix(
    asphaltBase,
    aggregateColor,
    clamp(0.5 + grain * aggregateIntensity, 0.0, 1.0)
  );

  let fullWidth = max(0.001, halfWidthMeters * 2.0);
  let halfLineUv = clamp(edgeLineHalfWidth / fullWidth, 0.0, 0.25);
  let centerOffsetUv = clamp((edgeLineShoulderMeters + edgeLineHalfWidth) / fullWidth, halfLineUv, 0.5);
  let aa = max(fwidth(trackUv.x) * 1.5, 0.001);
  let leftEdge = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(trackUv.x - centerOffsetUv));
  let rightEdge = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(trackUv.x - (1.0 - centerOffsetUv)));
  surface = mix(surface, edgeLineColor, max(leftEdge, rightEdge) * edgeLineOpacity);

  let laneDist = abs(trackUv.x - racingLineU);
  let rubberLane = 1.0 - smoothstep(0.0, max(0.001, rubberLaneWidth), laneDist);
  let rubberNoise = asphalt_fbm(vec2<f32>(
    laneDist * rubberLateralFreq,
    trackUv.y * rubberLongScale + 11.3
  ));
  let rubber = smoothstep(rubberThreshold, 1.0, rubberNoise) * rubberLane * rubberIntensity;
  surface = mix(surface, rubberColor, clamp(rubber, 0.0, 0.92));

  let oilLane = 1.0 - smoothstep(0.0, max(0.001, rubberLaneWidth * 1.6), laneDist);
  let oilNoise = asphalt_fbm(vec2<f32>(
    laneDist * 5.0 + 47.7,
    trackUv.y * oilLongScale + 73.1
  ));
  let oil = smoothstep(oilThreshold, 1.0, oilNoise) * oilLane * oilIntensity * 0.62;
  surface = mix(surface, oilColor, clamp(oil, 0.0, 0.75));

  return surface;
}
`, [asphaltFbmNode]);

const asphaltRoughnessNode = makeWgslFn(`
fn asphalt_roughness(
  trackUv: vec2<f32>,
  racingLineU: f32,
  baseRoughness: f32,
  rubberLaneWidth: f32,
  oilIntensity: f32,
  oilLongScale: f32,
  oilThreshold: f32,
  roughnessContrast: f32
) -> f32 {
  let laneDist = abs(trackUv.x - racingLineU);
  let roughnessNoise = asphalt_fbm(vec2<f32>(laneDist * 9.0, trackUv.y * 74.0));
  let oilLane = 1.0 - smoothstep(0.0, max(0.001, rubberLaneWidth * 1.6), laneDist);
  let oilNoise = asphalt_fbm(vec2<f32>(laneDist * 5.0 + 47.7, trackUv.y * oilLongScale + 73.1));
  let oil = smoothstep(oilThreshold, 1.0, oilNoise) * oilLane * oilIntensity;
  return clamp(
    baseRoughness + (roughnessNoise - 0.5) * 0.08 * roughnessContrast - oil * 0.32 * roughnessContrast,
    0.26,
    0.96
  );
}
`, [asphaltFbmNode]);

// Computes a view-space bump-perturbed normal from four pre-sampled height
// values (taken at fixed UV offsets — caller is responsible for the four
// `texture(grit).sample(grainUv ± epsilon)` calls so the GPU's mipmap +
// anisotropic filtering does the anti-aliasing). This mirrors what WebGL's
// `<normal_fragment_maps>` patch does at TrackRenderer.ts:1310-1372:
// fixed-offset central differencing of a mipmapped grit height, with the
// tangent basis derived from screen-space derivatives of position and UV.
//
// Why this and not TSL's built-in bumpMap(): bumpMap() re-samples the
// texture at `uv + dFdx(uv)` and `uv + dFdy(uv)`, which means the sample
// spacing shrinks with distance and the bump fades to flat far from the
// camera. WebGL avoids that by using a constant UV step, which is what we
// reproduce here.
const asphaltCentralBumpNode = makeWgslFn(`
fn asphalt_central_bump(
  hL: f32,
  hR: f32,
  hD: f32,
  hU: f32,
  posView: vec3<f32>,
  normView: vec3<f32>,
  uvAttr: vec2<f32>,
  bumpStrength: f32
) -> vec3<f32> {
  let dHdx = (hR - hL) * bumpStrength;
  let dHdy = (hU - hD) * bumpStrength;
  let Q1 = dpdx(posView);
  let Q2 = dpdy(posView);
  let St1 = dpdx(uvAttr);
  let St2 = dpdy(uvAttr);
  let T = normalize(Q1 * St2.y - Q2 * St1.y);
  let B = normalize(-Q1 * St2.x + Q2 * St1.x);
  return normalize(normView - T * dHdx - B * dHdy);
}
`);

const asphaltGlintNode = makeWgslFn(`
fn asphalt_glint(
  trackUv: vec2<f32>,
  racingLineU: f32,
  worldPosition: vec3<f32>,
  worldNormal: vec3<f32>,
  cameraWorldPosition: vec3<f32>,
  sunDir: vec3<f32>,
  glintColor: vec3<f32>,
  glintIntensity: f32,
  glintExponent: f32,
  glintStreakScale: f32,
  glintBroadness: f32,
  glintTextureInfluence: f32,
  rubberIntensity: f32,
  rubberLaneWidth: f32,
  oilIntensity: f32,
  oilLongScale: f32,
  oilThreshold: f32
) -> vec3<f32> {
  let normal = normalize(worldNormal);
  let viewDir = normalize(cameraWorldPosition - worldPosition);
  let sun = normalize(sunDir);
  let reflectedSun = reflect(-sun, normal);
  let spec = max(0.0, dot(viewDir, reflectedSun));
  let tight = pow(spec, max(1.0, glintExponent));
  let broadPower = mix(max(2.0, glintExponent * 0.32), 2.2, glintBroadness);
  let broad = pow(spec, broadPower) * glintBroadness * 0.72;
  let grazing = pow(1.0 - max(0.0, dot(viewDir, normal)), 1.65) * glintBroadness * 0.28;
  let sunFacing = smoothstep(-0.15, 0.35, dot(normal, sun));
  let across = abs(trackUv.x - 0.5) * 2.0;
  let lane = 1.0 - smoothstep(0.42, 1.0, across);
  let fold = abs(trackUv.x - racingLineU);
  let streak = smoothstep(0.18, 0.92, asphalt_fbm(vec2<f32>(fold * 3.2, trackUv.y * glintStreakScale * 0.16)));
  let rubberLane = 1.0 - smoothstep(0.0, max(0.001, rubberLaneWidth), fold);
  let oilLane = 1.0 - smoothstep(0.0, max(0.001, rubberLaneWidth * 1.6), fold);
  let oilNoise = asphalt_fbm(vec2<f32>(fold * 5.0 + 47.7, trackUv.y * oilLongScale + 73.1));
  let oil = smoothstep(oilThreshold, 1.0, oilNoise) * oilLane * oilIntensity;
  let sheen = glintBroadness * (0.05 + streak * 0.32);
  let textureResponse = clamp(0.42 + streak * 1.08 + oil * 1.85 - rubberLane * rubberIntensity * 0.34, 0.08, 2.35);
  let amount = (tight + broad + grazing + sheen)
    * sunFacing
    * lane
    * mix(1.0, textureResponse, glintTextureInfluence);
  return glintColor * glintIntensity * amount;
}
`, [asphaltFbmNode]);

// Composes procedural overlays ON TOP of a base colour (the baked albedo,
// or the synthesised asphaltBase fallback). Mirrors what the WebGL path
// does by shader-patching `<map_fragment>` then mixing the overlay colours
// in. Critically, this REPLACES the previous double-multiply approach where
// proceduralColor × bakedAlbedo crushed the road to near-black on WebGPU.
const bakedAsphaltColorNode = makeWgslFn(`
fn baked_asphalt_color(
  baseColor: vec3<f32>,
  trackUv: vec2<f32>,
  racingLineU: f32,
  halfWidthMeters: f32,
  gritPrimary: f32,
  gritSecondary: f32,
  materialMask: vec4<f32>,
  asphaltBase: vec3<f32>,
  aggregateColor: vec3<f32>,
  aggregateIntensity: f32,
  grainDetileMix: f32,
  edgeLineColor: vec3<f32>,
  edgeLineHalfWidth: f32,
  edgeLineShoulderMeters: f32,
  edgeLineOpacity: f32,
  rubberColor: vec3<f32>,
  rubberIntensity: f32,
  oilColor: vec3<f32>,
  oilIntensity: f32,
  worldPosition: vec3<f32>,
  cameraWorldPosition: vec3<f32>
) -> vec3<f32> {
  // Match the WebGL baked path: the visible road texture comes from the live
  // procedural grit field, while the baked maps provide low-frequency masks.
  // The albedo still contributes a small tint, but it must not dilute the grit
  // into a flat beige base.
  let grain = ((gritPrimary - 0.5) + (gritSecondary - 0.5) * grainDetileMix)
    * 2.0 / max(0.001, 1.0 + grainDetileMix);
  let proceduralMix = clamp(0.5 + grain * aggregateIntensity, 0.0, 1.0);
  let proceduralSurface = mix(asphaltBase, aggregateColor, proceduralMix);
  let fineGrit = clamp((gritPrimary - 0.5) * 3.0 + (gritSecondary - 0.5) * grainDetileMix, -0.85, 1.45);
  // Fleck math runs at much higher U-frequency than V-frequency. The V cell
  // size is tuned so that ~14 cells fall inside the visible road, which is
  // wide enough to read as discrete specks instead of sub-pixel aliasing.
  let fleckSeed = asphalt_hash(floor(vec2<f32>(trackUv.x * halfWidthMeters * 48.0, trackUv.y * 860.0)));
  let brightFleck = smoothstep(0.78, 0.995, fleckSeed);
  let darkFleck = 1.0 - smoothstep(0.04, 0.26, fleckSeed);
  // gritShading dynamic range widened from [0.26, 2.2] -> [0.18, 2.6]; the
  // narrow band was clipping all the bright flecks the WebGL path generates
  // and made the road read as one flat mid-gray. Brightening the highlights
  // and deepening the dark flecks reproduces the read-as-aggregate look.
  let gritShading = clamp(
    0.68
      + fineGrit * aggregateIntensity * 1.55
      + brightFleck * 1.45
      - darkFleck * 0.62,
    0.18,
    2.6
  );
  var surface = mix(proceduralSurface * gritShading, baseColor, 0.02);

  // Overlay 1: edge-line paint (shoulder stripes).
  let fullWidth = max(0.001, halfWidthMeters * 2.0);
  let halfLineUv = clamp(edgeLineHalfWidth / fullWidth, 0.0, 0.25);
  let centerOffsetUv = clamp((edgeLineShoulderMeters + edgeLineHalfWidth) / fullWidth, halfLineUv, 0.5);
  let aa = max(fwidth(trackUv.x) * 1.5, 0.001);
  let leftEdge = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(trackUv.x - centerOffsetUv));
  let rightEdge = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(trackUv.x - (1.0 - centerOffsetUv)));
  surface = mix(surface, edgeLineColor, max(leftEdge, rightEdge) * edgeLineOpacity);

  // Overlay 2: rubber streak — read from the baked material mask alpha
  // channel which marks where rubber buildup belongs along the lap.
  let rubber = clamp(materialMask.a * rubberIntensity, 0.0, 0.92);
  surface = mix(surface, rubberColor, rubber);

  // Overlay 3: oil sheen — fresnel-weighted, masked by material green.
  let oilViewDir = normalize(cameraWorldPosition - worldPosition);
  let fresnel = pow(1.0 - max(0.0, oilViewDir.y), 3.0);
  let oil = clamp(materialMask.g * oilIntensity * (0.4 + fresnel * 0.6), 0.0, 0.75);
  surface = mix(surface, oilColor, oil);
  return surface;
}
`, [asphaltHashNode]);

const bakedAsphaltRoughnessNode = makeWgslFn(`
fn baked_asphalt_roughness(
  materialMask: vec4<f32>,
  gritPrimary: f32,
  gritSecondary: f32,
  grainDetileMix: f32,
  roughnessContrast: f32
) -> f32 {
  let grain = ((gritPrimary - 0.5) + (gritSecondary - 0.5) * grainDetileMix)
    * 2.0 / max(0.001, 1.0 + grainDetileMix);
  return clamp(
    mix(0.58, materialMask.r, roughnessContrast) + grain * 0.12 * roughnessContrast,
    0.26,
    0.96
  );
}
`);

const bakedAsphaltGlintNode = makeWgslFn(`
fn baked_asphalt_glint(
  trackUv: vec2<f32>,
  materialMask: vec4<f32>,
  gritPrimary: f32,
  gritSecondary: f32,
  grainDetileMix: f32,
  worldPosition: vec3<f32>,
  worldNormal: vec3<f32>,
  cameraWorldPosition: vec3<f32>,
  sunDir: vec3<f32>,
  glintColor: vec3<f32>,
  glintIntensity: f32,
  glintExponent: f32,
  glintBroadness: f32,
  glintTextureInfluence: f32,
  rubberIntensity: f32,
  oilIntensity: f32
) -> vec3<f32> {
  let normal = normalize(worldNormal);
  let viewDir = normalize(cameraWorldPosition - worldPosition);
  let sun = normalize(sunDir);
  let reflectedSun = reflect(-sun, normal);
  let spec = max(0.0, dot(viewDir, reflectedSun));
  let tight = pow(spec, max(1.0, glintExponent));
  let broadPower = mix(max(2.0, glintExponent * 0.32), 2.2, glintBroadness);
  let broad = pow(spec, broadPower) * glintBroadness * 0.72;
  let grazing = pow(1.0 - max(0.0, dot(viewDir, normal)), 1.65) * glintBroadness * 0.28;
  let sunFacing = smoothstep(-0.15, 0.35, dot(normal, sun));
  let across = abs(trackUv.x - 0.5) * 2.0;
  let lane = 1.0 - smoothstep(0.42, 1.0, across);
  let glintMask = smoothstep(0.18, 0.92, materialMask.b);
  let grain = ((gritPrimary - 0.5) + (gritSecondary - 0.5) * grainDetileMix)
    * 2.0 / max(0.001, 1.0 + grainDetileMix);
  let fineSparkle = smoothstep(0.08, 0.78, grain);
  let oil = materialMask.g * oilIntensity;
  let rubber = materialMask.a * rubberIntensity;
  let sheen = glintBroadness * (0.05 + glintMask * 0.32) + fineSparkle * 0.13;
  let textureResponse = clamp(0.42 + glintMask * 1.08 + fineSparkle * 1.35 + oil * 1.85 - rubber * 0.34, 0.08, 2.85);
  let amount = (tight + broad + grazing + sheen)
    * sunFacing
    * lane
    * mix(1.0, textureResponse, glintTextureInfluence);
  return glintColor * glintIntensity * amount;
}
`);

function makeWebGPUBakedAsphaltNodeMaterial(
  style: StyleConfig,
  surface: BakedPbrSurface,
  lapCycles: number,
  liveDetail: boolean,
): MeshStandardNodeMaterial {
  const uniforms = makeWebGPUAsphaltUniforms(style);
  const material = new MeshStandardNodeMaterial({
    color: 0xffffff,
    side: THREE.DoubleSide,
    vertexColors: true,
    roughness: style.asphalt.baseRoughness,
    metalness: 0,
    envMapIntensity: style.asphalt.baseEnvIntensity,
    toneMapped: true,
  });
  material.map = uniforms.gritTexture.value as THREE.Texture;
  if (surface.normalUrl) {
    const normalTex = loadBakedPbrTexture(surface.normalUrl, lapCycles, false);
    material.normalMap = normalTex;
    const scale = surface.normalScale ?? 1;
    material.normalScale = new THREE.Vector2(scale, scale);
  }
  // The colorNode override replaces the whole built-in map pipeline, so baked
  // textures that are sampled manually must use normalized full-lap UVs here
  // instead of Texture.repeat. Otherwise the TSL texture transform scales the
  // already-normalized lap UV a second time and the road samples one flat row.
  let bakedAlbedoSample: any = null;
  if (surface.albedoUrl) {
    const albedoTex = loadBakedPbrNodeSampleTexture(surface.albedoUrl, true);
    bakedAlbedoSample = makeNodeTexture(albedoTex).bias(taauMipBiasUniform);
    material.userData.webgpuAsphaltAlbedoTexture = albedoTex;
  }
  const trackUv = makeNodeUv();
  const racingLineU = makeNodeAttribute('aRacingLineU', 'float');
  const trackHalfWidth = makeNodeAttribute('aTrackHalfWidth', 'float');
  const grainUv = makeNodeVec2(
    trackUv.x.sub(0.5).mul(2.0).mul(trackHalfWidth).div(uniforms.grainTileMeters),
    trackUv.y.mul(6.0).div(uniforms.grainTileMeters),
  );
  const grainUvDetile = grainUv.mul(0.4137).add(makeNodeVec2(31.7, 17.3));
  const gritPrimary = uniforms.gritTexture.sample(grainUv).r;
  const gritSecondary = uniforms.gritTexture.sample(grainUvDetile).r;
  const materialTexture = surface.materialUrl
    ? makeNodeTexture(loadBakedPbrNodeSampleTexture(surface.materialUrl, false)).bias(taauMipBiasUniform)
    : null;
  const materialMask = materialTexture
    ? materialTexture.sample(makeNodeVec2(trackUv.x, trackUv.y.div(Math.max(1, lapCycles))))
    : makeNodeVec4(0.7, 0.0, 0.0, 0.0);

  // Use procedural grit as the visible base, with the baked albedo only as a
  // low-frequency tint. WebGL's baked shader never lets the albedo wash out the
  // live grit; doing so is what made the WebGPU road look textureless.
  const bakedAlbedoColor = bakedAlbedoSample
    ? bakedAlbedoSample.sample(makeNodeVec2(trackUv.x, trackUv.y.div(Math.max(1, lapCycles)))).rgb
    : uniforms.asphaltBase;
  const overlaid = bakedAsphaltColorNode({
    baseColor: bakedAlbedoColor,
    trackUv,
    racingLineU,
    halfWidthMeters: trackHalfWidth,
    gritPrimary,
    gritSecondary,
    materialMask,
    asphaltBase: uniforms.asphaltBase,
    aggregateColor: uniforms.aggregateColor,
    aggregateIntensity: uniforms.aggregateIntensity,
    grainDetileMix: uniforms.grainDetileMix,
    edgeLineColor: uniforms.edgeLineColor,
    edgeLineHalfWidth: uniforms.edgeLineHalfWidth,
    edgeLineShoulderMeters: uniforms.edgeLineShoulderMeters,
    edgeLineOpacity: uniforms.edgeLineOpacity,
    rubberColor: uniforms.rubberColor,
    rubberIntensity: uniforms.rubberIntensity,
    oilColor: uniforms.oilColor,
    oilIntensity: uniforms.oilIntensity,
    worldPosition: positionWorld,
    cameraWorldPosition: cameraPosition,
  });
  // Per-vertex wear scalar (exhaust streaks + edge debris) applied last,
  // mirroring WebGL's `<color_fragment>` chunk position.
  material.colorNode = overlaid.mul(makeNodeVertexColor().rgb);
  // Tracer flags so tests + future code reviews can verify which
  // composition the build actually took. See the matching expectations
  // in TrackRenderer.test.ts. The precise wording matters — tests pin
  // these exact strings to lock the behaviour against silent regression.
  material.userData.webgpuAsphaltColorComposition = bakedAlbedoSample
    ? 'procedural-grit-base'
    : 'procedural-only';
  material.userData.webgpuAsphaltVertexColorWearApplied = true;
  material.roughnessNode = bakedAsphaltRoughnessNode({
    materialMask,
    gritPrimary,
    gritSecondary,
    grainDetileMix: uniforms.grainDetileMix,
    roughnessContrast: uniforms.trackRoughnessContrast,
  });
  // Sample the grit DataTexture at four FIXED UV offsets, then perturb the
  // view-space normal from those samples. This is what WebGL does at
  // <normal_fragment_maps>: constant-step central differencing so the bump
  // amplitude stays consistent regardless of distance. TSL's bumpMap()
  // helper uses screen-space dFdx(uv) instead, which collapses the sample
  // spacing to zero far from the camera and leaves the road flat. The
  // texture samples here still go through the GPU's mipmap + 16x aniso
  // filter, so distance anti-aliasing is preserved without the moiré of
  // computing everything from a per-fragment hash. The baked normal map
  // (assets/baked/surfaces-pbr/twin-suns-autodrome/asphalt-normal.webp) is
  // uniform blue with no grit, so there is nothing meaningful to compose
  // with; the bump alone supplies the per-pixel normal variation.
  // Offsets are in TRACK UV space (matching WebGL's asphaltBumpStep), and
  // the grit UV is recomputed from each offset trackUv using the SAME
  // transform WebGL's asphaltMicroHeight uses for its grit fetch — denom 6
  // across U, raw trackUv.y across V. We use grainUv for the color path
  // (denser tiling for color variation) but the bump path needs WebGL's
  // wider grit footprint so the bump strength comes out comparable.
  const bumpEpsU = 0.0022;
  const bumpEpsV = 0.0009;
  const bumpGritUvAt = (du: number, dv: number): any => {
    const tu = trackUv.add(makeNodeVec2(du, dv));
    return makeNodeVec2(
      tu.x.sub(0.5).mul(2.0).mul(trackHalfWidth).div(6.0),
      tu.y,
    );
  };
  // Bump strength comes from uniforms.trackBumpScale so the tuning slider
  // (StylePanel → trackGlint.normalStrength) drives this material live, the
  // same way it drives the WebGL bump in installBakedAsphaltShader.
  material.normalNode = asphaltCentralBumpNode({
    hL: uniforms.gritTexture.sample(bumpGritUvAt(-bumpEpsU, 0)).r,
    hR: uniforms.gritTexture.sample(bumpGritUvAt(bumpEpsU, 0)).r,
    hD: uniforms.gritTexture.sample(bumpGritUvAt(0, -bumpEpsV)).r,
    hU: uniforms.gritTexture.sample(bumpGritUvAt(0, bumpEpsV)).r,
    posView: positionView,
    normView: normalView,
    uvAttr: trackUv,
    bumpStrength: uniforms.trackBumpScale,
  });
  material.userData.webgpuAsphaltNormalSource = 'bump';
  // Identify the bump kernel so tests can pin it. Regressing to TSL's
  // built-in bumpMap() silently re-introduces the screen-space-dFdx flaw
  // where the road goes flat at distance; the unit test asserts on this
  // string to keep us on the fixed-offset central-difference path.
  material.userData.webgpuAsphaltBumpKernel = 'central-fixed-offset';
  void liveDetail;
  material.emissiveNode = bakedAsphaltGlintNode({
    trackUv,
    materialMask,
    gritPrimary,
    gritSecondary,
    grainDetileMix: uniforms.grainDetileMix,
    worldPosition: positionWorld,
    worldNormal: normalWorld,
    cameraWorldPosition: cameraPosition,
    sunDir: uniforms.trackGlintSunDir,
    glintColor: uniforms.trackGlintColor,
    glintIntensity: uniforms.trackGlintIntensity,
    glintExponent: uniforms.trackGlintExponent,
    glintBroadness: uniforms.trackGlintBroadness,
    glintTextureInfluence: uniforms.trackGlintTextureInfluence,
    rubberIntensity: uniforms.rubberIntensity,
    oilIntensity: uniforms.oilIntensity,
  });
  material.userData.webgpuAsphaltUniforms = uniforms;
  material.userData.trackGlintUniforms = uniforms;
  material.userData.bakedPbrSurface = surface;
  material.userData.bakedAsphaltLiveDetail = liveDetail;
  return material;
}

function makeWebGPUAsphaltNodeMaterial(style: StyleConfig): MeshStandardNodeMaterial {
  const uniforms = makeWebGPUAsphaltUniforms(style);
  const material = new MeshStandardNodeMaterial({
    color: 0xffffff,
    side: THREE.DoubleSide,
    vertexColors: true,
    roughness: style.asphalt.baseRoughness,
    metalness: 0,
    envMapIntensity: style.asphalt.baseEnvIntensity,
    toneMapped: true,
  });
  const trackUv = makeNodeUv();
  const racingLineU = makeNodeAttribute('aRacingLineU', 'float');
  const trackHalfWidth = makeNodeAttribute('aTrackHalfWidth', 'float');
  const grainUv = makeNodeVec2(
    trackUv.x.sub(0.5).mul(2.0).mul(trackHalfWidth).div(uniforms.grainTileMeters),
    trackUv.y.mul(6.0).div(uniforms.grainTileMeters),
  );
  const grainUvDetile = grainUv.mul(0.4137).add(makeNodeVec2(31.7, 17.3));
  const gritPrimary = uniforms.gritTexture.sample(grainUv).r;
  const gritSecondary = uniforms.gritTexture.sample(grainUvDetile).r;

  // See note in makeWebGPUBakedAsphaltNodeMaterial — `<color_fragment>` parity.
  material.colorNode = asphaltColorNode({
    trackUv,
    racingLineU,
    halfWidthMeters: trackHalfWidth,
    gritPrimary,
    gritSecondary,
    asphaltBase: uniforms.asphaltBase,
    aggregateColor: uniforms.aggregateColor,
    aggregateIntensity: uniforms.aggregateIntensity,
    grainDetileMix: uniforms.grainDetileMix,
    edgeLineColor: uniforms.edgeLineColor,
    edgeLineHalfWidth: uniforms.edgeLineHalfWidth,
    edgeLineShoulderMeters: uniforms.edgeLineShoulderMeters,
    edgeLineOpacity: uniforms.edgeLineOpacity,
    rubberColor: uniforms.rubberColor,
    rubberIntensity: uniforms.rubberIntensity,
    rubberLongScale: uniforms.rubberLongScale,
    rubberLateralFreq: uniforms.rubberLateralFreq,
    rubberLaneWidth: uniforms.rubberLaneWidth,
    rubberThreshold: uniforms.rubberThreshold,
    oilColor: uniforms.oilColor,
    oilIntensity: uniforms.oilIntensity,
    oilLongScale: uniforms.oilLongScale,
    oilThreshold: uniforms.oilThreshold,
  }).mul(makeNodeVertexColor().rgb);
  material.roughnessNode = asphaltRoughnessNode({
    trackUv,
    racingLineU,
    baseRoughness: uniforms.baseRoughness,
    rubberLaneWidth: uniforms.rubberLaneWidth,
    oilIntensity: uniforms.oilIntensity,
    oilLongScale: uniforms.oilLongScale,
    oilThreshold: uniforms.oilThreshold,
    roughnessContrast: uniforms.trackRoughnessContrast,
  });
  {
    // Procedural-only path uses the same fixed-offset central differencing
    // and the same trackBumpScale uniform as the baked path, so the
    // normalStrength tuning slider drives both materials identically.
    const bumpEpsU = 0.0022;
    const bumpEpsV = 0.0009;
    const bumpGritUvAt = (du: number, dv: number): any => {
      const tu = trackUv.add(makeNodeVec2(du, dv));
      return makeNodeVec2(
        tu.x.sub(0.5).mul(2.0).mul(trackHalfWidth).div(6.0),
        tu.y,
      );
    };
    material.normalNode = asphaltCentralBumpNode({
      hL: uniforms.gritTexture.sample(bumpGritUvAt(-bumpEpsU, 0)).r,
      hR: uniforms.gritTexture.sample(bumpGritUvAt(bumpEpsU, 0)).r,
      hD: uniforms.gritTexture.sample(bumpGritUvAt(0, -bumpEpsV)).r,
      hU: uniforms.gritTexture.sample(bumpGritUvAt(0, bumpEpsV)).r,
      posView: positionView,
      normView: normalView,
      uvAttr: trackUv,
      bumpStrength: uniforms.trackBumpScale,
    });
  }
  material.emissiveNode = asphaltGlintNode({
    trackUv,
    racingLineU,
    worldPosition: positionWorld,
    worldNormal: normalWorld,
    cameraWorldPosition: cameraPosition,
    sunDir: uniforms.trackGlintSunDir,
    glintColor: uniforms.trackGlintColor,
    glintIntensity: uniforms.trackGlintIntensity,
    glintExponent: uniforms.trackGlintExponent,
    glintStreakScale: uniforms.trackGlintStreakScale,
    glintBroadness: uniforms.trackGlintBroadness,
    glintTextureInfluence: uniforms.trackGlintTextureInfluence,
    rubberIntensity: uniforms.rubberIntensity,
    rubberLaneWidth: uniforms.rubberLaneWidth,
    oilIntensity: uniforms.oilIntensity,
    oilLongScale: uniforms.oilLongScale,
    oilThreshold: uniforms.oilThreshold,
  });
  material.userData.webgpuAsphaltUniforms = uniforms;
  material.userData.trackGlintUniforms = uniforms;
  // Procedural-only path: synthesises both colour and normal from noise.
  // Tracer flags match those on the baked path so the test assertions
  // can describe the procedural fallback explicitly.
  material.userData.webgpuAsphaltColorComposition = 'procedural-only';
  material.userData.webgpuAsphaltVertexColorWearApplied = true;
  material.userData.webgpuAsphaltNormalSource = 'bump';
  return material;
}

function makeWebGPUAsphaltUniforms(style: StyleConfig): WebGPUAsphaltUniforms {
  const gritTexture = makeProceduralTexture(style);
  gritTexture.name = 'webgpu-asphalt-grit';
  return {
    gritTexture: makeNodeTexture(gritTexture).bias(taauMipBiasUniform),
    asphaltBase: makeNodeUniform(colorVector(style.asphalt.baseHex)),
    aggregateColor: makeNodeUniform(colorVector(style.asphalt.aggregateColorHex)),
    aggregateIntensity: makeNodeUniform(
      style.asphalt.aggregateIntensity * (style.asphalt.webgpuAggregateMultiplier ?? 1.0),
    ),
    grainTileMeters: makeNodeUniform(style.asphalt.grainTileMeters),
    grainDetileMix: makeNodeUniform(style.asphalt.grainDetileMix),
    edgeLineColor: makeNodeUniform(colorVector(style.edgeLines.colorHex)),
    edgeLineHalfWidth: makeNodeUniform(style.edgeLines.width * 0.5),
    edgeLineShoulderMeters: makeNodeUniform(EDGE_LINE_ASPHALT_SHOULDER),
    edgeLineOpacity: makeNodeUniform(THREE.MathUtils.clamp(0.72 + style.edgeLines.emissiveStrength * 0.24, 0, 1)),
    rubberColor: makeNodeUniform(colorVector(style.asphalt.rubberStreakHex)),
    rubberIntensity: makeNodeUniform(style.asphalt.rubberStreakIntensity),
    rubberLongScale: makeNodeUniform(style.asphalt.rubberStreakLongScale),
    rubberLateralFreq: makeNodeUniform(style.asphalt.rubberStreakLateralFreq),
    rubberLaneWidth: makeNodeUniform(style.asphalt.rubberStreakLaneWidth),
    rubberThreshold: makeNodeUniform(style.asphalt.rubberStreakThreshold),
    oilColor: makeNodeUniform(colorVector(style.asphalt.oilSheenHex)),
    oilIntensity: makeNodeUniform(style.asphalt.oilSheenIntensity),
    oilLongScale: makeNodeUniform(style.asphalt.oilSheenLongScale),
    oilThreshold: makeNodeUniform(style.asphalt.oilSheenThreshold),
    trackGlintColor: makeNodeUniform(colorVector(style.trackGlint.colorHex)),
    trackGlintIntensity: makeNodeUniform(style.trackGlint.intensity),
    trackGlintExponent: makeNodeUniform(style.trackGlint.exponent),
    trackGlintStreakScale: makeNodeUniform(style.trackGlint.streakScale),
    trackGlintBroadness: makeNodeUniform(style.trackGlint.broadness),
    trackGlintTextureInfluence: makeNodeUniform(style.trackGlint.textureInfluence),
    // Same effective scale WebGL applies in installBakedAsphaltShader:
    // `uTrackBumpStrength * (0.28 + aggregateIntensity * 0.54 + oil * 0.24)`
    // resolves to ≈ 0.56× with the defaults. We pre-multiply here so the
    // node graph reads a single number, then live-edit via the slider in
    // applyWebGPUAsphaltStyle. webgpuBumpMultiplier (default 1.0) is the
    // WebGPU-only knob — WebGL never reads it, so dialing here cannot
    // regress the WebGL look.
    trackBumpScale: makeNodeUniform(
      style.trackGlint.normalStrength * 0.56 * (style.trackGlint.webgpuBumpMultiplier ?? 1.0),
    ),
    trackRoughnessContrast: makeNodeUniform(style.trackGlint.roughnessContrast),
    trackGlintSunDir: makeNodeUniform(sunDirectionVector(style)),
    baseRoughness: makeNodeUniform(style.asphalt.baseRoughness),
  };
}

function colorVector(hex: string): THREE.Vector3 {
  const color = new THREE.Color(hex);
  return new THREE.Vector3(color.r, color.g, color.b);
}

function setColorVector(target: THREE.Vector3, hex: string): void {
  const color = new THREE.Color(hex);
  target.set(color.r, color.g, color.b);
}

function sunDirectionVector(style: StyleConfig): THREE.Vector3 {
  return new THREE.Vector3(style.sky.sunDir.x, style.sky.sunDir.y, style.sky.sunDir.z).normalize();
}

function installBakedAsphaltShader(
  material: THREE.MeshStandardMaterial,
  style: StyleConfig,
  materialMap: THREE.Texture,
  lapCycles: number,
  options: { liveDetail: boolean },
): void {
  const uniforms = makeBakedAsphaltUniforms(style, materialMap, lapCycles);
  material.userData.bakedAsphaltUniforms = uniforms;
  material.userData.bakedAsphaltLiveDetail = options.liveDetail;
  material.userData.trackGlintUniforms = uniforms;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace(
        'void main() {',
        `
attribute float aRacingLineU;
attribute float aTrackHalfWidth;
varying vec3 vBakedWorldPosition;
varying vec3 vBakedWorldNormal;
varying vec2 vBakedTrackUv;
varying float vBakedRacingLineU;
varying float vBakedTrackHalfWidth;

void main() {
        `.trim(),
      )
      .replace(
        '#include <defaultnormal_vertex>',
        `
#include <defaultnormal_vertex>
vBakedWorldNormal = normalize(transformedNormal);
        `.trim(),
      )
      .replace(
        '#include <uv_vertex>',
        `
#include <uv_vertex>
vBakedTrackUv = uv;
vBakedRacingLineU = aRacingLineU;
vBakedTrackHalfWidth = aTrackHalfWidth;
        `.trim(),
      )
      .replace(
        '#include <worldpos_vertex>',
        `
#include <worldpos_vertex>
vBakedWorldPosition = worldPosition.xyz;
        `.trim(),
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        `
uniform sampler2D uBakedMaterialMap;
uniform float uBakedMaterialVScale;
uniform vec3 uAsphaltBase;
uniform vec3 uAggregateColor;
uniform float uAggregateIntensity;
uniform float uGrainTileMeters;
uniform float uGrainDetileMix;
uniform vec3 uEdgeLineColor;
uniform float uEdgeLineHalfWidth;
uniform float uEdgeLineShoulderMeters;
uniform float uEdgeLineOpacity;
uniform vec3 uRubberColor;
uniform float uRubberIntensity;
uniform float uRubberLaneWidth;
uniform vec3 uOilColor;
uniform float uOilIntensity;
uniform vec3 uTrackGlintColor;
uniform float uTrackGlintIntensity;
uniform float uTrackGlintExponent;
uniform float uTrackGlintStreakScale;
uniform float uTrackGlintBroadness;
uniform float uTrackGlintTextureInfluence;
uniform float uTrackBumpStrength;
uniform float uTrackRoughnessContrast;
uniform vec3 uTrackGlintSunDir;
varying vec3 vBakedWorldPosition;
varying vec3 vBakedWorldNormal;
varying vec2 vBakedTrackUv;
varying float vBakedRacingLineU;
varying float vBakedTrackHalfWidth;

float bakedAsphaltHash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float bakedAsphaltNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = bakedAsphaltHash(i);
  float b = bakedAsphaltHash(i + vec2(1.0, 0.0));
  float c = bakedAsphaltHash(i + vec2(0.0, 1.0));
  float d = bakedAsphaltHash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float bakedAsphaltFbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * bakedAsphaltNoise(p);
    p *= 2.03;
    a *= 0.5;
  }
  return v;
}

float bakedEdgeLineMask(vec2 uv, float halfWidthMeters) {
  float fullWidth = max(0.001, halfWidthMeters * 2.0);
  float halfLineUv = clamp(uEdgeLineHalfWidth / fullWidth, 0.0, 0.25);
  float centerOffsetUv = clamp((uEdgeLineShoulderMeters + uEdgeLineHalfWidth) / fullWidth, halfLineUv, 0.5);
  float leftCenter = centerOffsetUv;
  float rightCenter = 1.0 - centerOffsetUv;
  float aa = max(fwidth(uv.x) * 1.5, 0.001);
  float left = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(uv.x - leftCenter));
  float right = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(uv.x - rightCenter));
  return max(left, right);
}

float asphaltMicroHeight(vec2 p) {
  float grit = 0.5;
#ifdef USE_MAP
  vec2 gritUv = vec2((p.x - 0.5) * 2.0 * vBakedTrackHalfWidth / 6.0, p.y);
  grit = texture2D(map, gritUv).r;
#endif
  float laneFold = abs(p.x - vBakedRacingLineU);
  float longitudinal = bakedAsphaltFbm(vec2(laneFold * 210.0 + 4.7, p.y * 18.0));
  float fineGroove = bakedAsphaltFbm(vec2(laneFold * 92.0, p.y * 36.0 + longitudinal * 3.0));
  float lane = 1.0 - smoothstep(0.0, uRubberLaneWidth * 1.8, laneFold);
  return (grit - 0.5) * 0.62
    + (longitudinal - 0.5) * 0.34
    + (fineGroove - 0.5) * 0.18
    - lane * 0.10;
}

vec3 asphaltBakedDirectGlint(
  vec3 lightColor,
  vec3 lightDirView,
  vec3 normalView,
  vec3 viewDirView,
  float oilMask,
  float glintMask,
  float rubberMask
) {
  vec3 sunDirView = normalize((viewMatrix * vec4(normalize(uTrackGlintSunDir), 0.0)).xyz);
  float sunLightAlignment = smoothstep(0.58, 0.96, abs(dot(normalize(lightDirView), sunDirView)));
  vec3 trackGlintReflection = reflect(-normalize(lightDirView), normalize(normalView));
  float trackGlintSpec = max(0.0, dot(normalize(viewDirView), trackGlintReflection));
  float trackGlintTight = pow(trackGlintSpec, max(1.0, uTrackGlintExponent));
  float trackGlintBroadPower = mix(max(2.0, uTrackGlintExponent * 0.32), 2.2, uTrackGlintBroadness);
  float trackGlintBroad = pow(trackGlintSpec, trackGlintBroadPower) * uTrackGlintBroadness * 0.72;
  float trackGlintGrazing = pow(1.0 - max(0.0, dot(normalize(viewDirView), normalize(normalView))), 1.65) * uTrackGlintBroadness * 0.28;
  float trackGlintSunFacing = smoothstep(-0.15, 0.35, dot(normalize(normalView), normalize(lightDirView)));
  float trackGlintAcross = abs(vBakedTrackUv.x - 0.5) * 2.0;
  float trackGlintLane = 1.0 - smoothstep(0.42, 1.0, trackGlintAcross);
  float trackGlintFold = abs(vBakedTrackUv.x - vBakedRacingLineU);
  ${options.liveDetail ? `
  float liveGlintStreak = bakedAsphaltFbm(vec2(trackGlintFold * 3.2, vBakedTrackUv.y * uTrackGlintStreakScale * 0.16));
  float trackGlintStreak = smoothstep(0.18, 0.92, max(liveGlintStreak, glintMask));
  `.trim() : `
  float trackGlintStreak = smoothstep(0.18, 0.92, glintMask);
  `.trim()}
  float trackGlintSheen = uTrackGlintBroadness * (0.05 + trackGlintStreak * 0.32);
  float textureResponse = clamp(
    0.42 + trackGlintStreak * 1.08 + oilMask * 1.85 - rubberMask * 0.34,
    0.08,
    2.35
  );
  float trackGlintAmount = (trackGlintTight + trackGlintBroad + trackGlintGrazing + trackGlintSheen)
    * trackGlintSunFacing
    * sunLightAlignment
    * trackGlintLane
    * mix(1.0, textureResponse, uTrackGlintTextureInfluence);
  return uTrackGlintColor * lightColor * uTrackGlintIntensity * trackGlintAmount;
}

void main() {
vec4 asphaltBakedMaterial = texture2D(
  uBakedMaterialMap,
  vec2(vBakedTrackUv.x, vBakedTrackUv.y * uBakedMaterialVScale)
);
float asphaltBakedOilMask = asphaltBakedMaterial.g;
float asphaltBakedGlintMask = asphaltBakedMaterial.b;
float asphaltBakedRubberMask = asphaltBakedMaterial.a;
float asphaltBakedOil = asphaltBakedOilMask * uOilIntensity;
float asphaltBakedRubber = asphaltBakedRubberMask * uRubberIntensity;
        `.trim(),
      )
      .replace(
        '#include <map_fragment>',
        `
float grainTile = max(0.5, uGrainTileMeters);
vec2 grainUv = vec2(
  (vBakedTrackUv.x - 0.5) * 2.0 * vBakedTrackHalfWidth / grainTile,
  vBakedTrackUv.y * 6.0 / grainTile
);
#ifdef USE_MAP
  vec4 sampledDiffuseColor = texture2D(map, grainUv);
  vec2 grainUvDetile = grainUv * 0.4137 + vec2(31.7, 17.3);
  float grainDetileSample = texture2D(map, grainUvDetile).r;
  float grainPrimary = sampledDiffuseColor.r - 0.5;
  float grainSecondary = grainDetileSample - 0.5;
  float grain = (grainPrimary + grainSecondary * uGrainDetileMix)
    * 2.0 / (1.0 + uGrainDetileMix);
  vec3 surface = mix(
    uAsphaltBase,
    uAggregateColor,
    clamp(0.5 + grain * uAggregateIntensity, 0.0, 1.0)
  );

  float edgeMask = bakedEdgeLineMask(vBakedTrackUv, vBakedTrackHalfWidth);
  surface = mix(surface, uEdgeLineColor, edgeMask * uEdgeLineOpacity);
  surface = mix(surface, uRubberColor, asphaltBakedRubber);

  vec3 oilViewDir = normalize(cameraPosition - vBakedWorldPosition);
  float fresnel = pow(1.0 - max(0.0, oilViewDir.y), 3.0);
  float oil = asphaltBakedOil * (0.4 + fresnel * 0.6);
  surface = mix(surface, uOilColor, oil);

  diffuseColor.rgb = surface;
#endif
        `.trim(),
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `
#include <roughnessmap_fragment>
roughnessFactor = clamp(asphaltBakedMaterial.r, 0.26, 0.96);
        `.trim(),
      )
      .replace(
        '#include <normal_fragment_maps>',
        options.liveDetail ? `
#include <normal_fragment_maps>
vec2 asphaltBumpStep = vec2(0.0022, 0.0009);
float asphaltHeightL = asphaltMicroHeight(vBakedTrackUv - vec2(asphaltBumpStep.x, 0.0));
float asphaltHeightR = asphaltMicroHeight(vBakedTrackUv + vec2(asphaltBumpStep.x, 0.0));
float asphaltHeightD = asphaltMicroHeight(vBakedTrackUv - vec2(0.0, asphaltBumpStep.y));
float asphaltHeightU = asphaltMicroHeight(vBakedTrackUv + vec2(0.0, asphaltBumpStep.y));
vec2 asphaltHeightGradient = vec2(asphaltHeightR - asphaltHeightL, asphaltHeightU - asphaltHeightD);

vec3 asphaltQ1 = dFdx(vBakedWorldPosition);
vec3 asphaltQ2 = dFdy(vBakedWorldPosition);
vec2 asphaltSt1 = dFdx(vBakedTrackUv);
vec2 asphaltSt2 = dFdy(vBakedTrackUv);
vec3 asphaltTangent = normalize(asphaltQ1 * asphaltSt2.t - asphaltQ2 * asphaltSt1.t);
vec3 asphaltBitangent = normalize(-asphaltQ1 * asphaltSt2.s + asphaltQ2 * asphaltSt1.s);
float asphaltBumpStrength = uTrackBumpStrength * (0.35 + uAggregateIntensity * 0.72 + asphaltBakedOil * 0.34);
vec3 asphaltWorldNormal = normalize(
  vBakedWorldNormal
  - asphaltTangent * asphaltHeightGradient.x * asphaltBumpStrength
  - asphaltBitangent * asphaltHeightGradient.y * asphaltBumpStrength
);
normal = normalize((viewMatrix * vec4(asphaltWorldNormal, 0.0)).xyz);
        `.trim() : `
#include <normal_fragment_maps>
vec2 asphaltBumpStep = vec2(0.0022, 0.0009);
vec2 asphaltPointL = vBakedTrackUv - vec2(asphaltBumpStep.x, 0.0);
vec2 asphaltPointR = vBakedTrackUv + vec2(asphaltBumpStep.x, 0.0);
vec2 asphaltPointD = vBakedTrackUv - vec2(0.0, asphaltBumpStep.y);
vec2 asphaltPointU = vBakedTrackUv + vec2(0.0, asphaltBumpStep.y);
float asphaltGritL = 0.5;
float asphaltGritR = 0.5;
float asphaltGritD = 0.5;
float asphaltGritU = 0.5;
#ifdef USE_MAP
asphaltGritL = texture2D(map, vec2((asphaltPointL.x - 0.5) * 2.0 * vBakedTrackHalfWidth / 6.0, asphaltPointL.y)).r;
asphaltGritR = texture2D(map, vec2((asphaltPointR.x - 0.5) * 2.0 * vBakedTrackHalfWidth / 6.0, asphaltPointR.y)).r;
asphaltGritD = texture2D(map, vec2((asphaltPointD.x - 0.5) * 2.0 * vBakedTrackHalfWidth / 6.0, asphaltPointD.y)).r;
asphaltGritU = texture2D(map, vec2((asphaltPointU.x - 0.5) * 2.0 * vBakedTrackHalfWidth / 6.0, asphaltPointU.y)).r;
#endif
float asphaltLaneL = 1.0 - smoothstep(0.0, uRubberLaneWidth * 1.8, abs(asphaltPointL.x - vBakedRacingLineU));
float asphaltLaneR = 1.0 - smoothstep(0.0, uRubberLaneWidth * 1.8, abs(asphaltPointR.x - vBakedRacingLineU));
float asphaltLaneD = 1.0 - smoothstep(0.0, uRubberLaneWidth * 1.8, abs(asphaltPointD.x - vBakedRacingLineU));
float asphaltLaneU = 1.0 - smoothstep(0.0, uRubberLaneWidth * 1.8, abs(asphaltPointU.x - vBakedRacingLineU));
float asphaltHeightL = (asphaltGritL - 0.5) * 0.82 - asphaltLaneL * 0.10;
float asphaltHeightR = (asphaltGritR - 0.5) * 0.82 - asphaltLaneR * 0.10;
float asphaltHeightD = (asphaltGritD - 0.5) * 0.82 - asphaltLaneD * 0.10;
float asphaltHeightU = (asphaltGritU - 0.5) * 0.82 - asphaltLaneU * 0.10;
vec2 asphaltHeightGradient = vec2(asphaltHeightR - asphaltHeightL, asphaltHeightU - asphaltHeightD);

vec3 asphaltQ1 = dFdx(vBakedWorldPosition);
vec3 asphaltQ2 = dFdy(vBakedWorldPosition);
vec2 asphaltSt1 = dFdx(vBakedTrackUv);
vec2 asphaltSt2 = dFdy(vBakedTrackUv);
vec3 asphaltTangent = normalize(asphaltQ1 * asphaltSt2.t - asphaltQ2 * asphaltSt1.t);
vec3 asphaltBitangent = normalize(-asphaltQ1 * asphaltSt2.s + asphaltQ2 * asphaltSt1.s);
float asphaltBumpStrength = uTrackBumpStrength * (0.28 + uAggregateIntensity * 0.54 + asphaltBakedOil * 0.24);
vec3 asphaltWorldNormal = normalize(
  vBakedWorldNormal
  - asphaltTangent * asphaltHeightGradient.x * asphaltBumpStrength
  - asphaltBitangent * asphaltHeightGradient.y * asphaltBumpStrength
);
normal = normalize((viewMatrix * vec4(asphaltWorldNormal, 0.0)).xyz);
        `.trim(),
      )
      .replace(
        '#include <lights_fragment_begin>',
        THREE.ShaderChunk.lights_fragment_begin.replaceAll(
          'RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );',
          `
RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
reflectedLight.directSpecular += asphaltBakedDirectGlint(
  directLight.color,
  directLight.direction,
  geometryNormal,
  geometryViewDir,
  asphaltBakedOil,
  asphaltBakedGlintMask,
  asphaltBakedRubber
);
          `.trim(),
        ),
      );
  };
  const prevCacheKey = material.customProgramCacheKey?.bind(material);
  material.customProgramCacheKey = () => `${prevCacheKey ? prevCacheKey() : ''}|asphalt-baked-pbr-v2|live-${options.liveDetail ? 'on' : 'off'}`;
}

function makeBakedAsphaltUniforms(
  style: StyleConfig,
  materialMap: THREE.Texture,
  lapCycles: number,
): BakedAsphaltUniforms {
  return {
    uBakedMaterialMap: { value: materialMap },
    uBakedMaterialVScale: { value: 1 / Math.max(1, lapCycles) },
    uAsphaltBase: { value: new THREE.Color(style.asphalt.baseHex) },
    uAggregateColor: { value: new THREE.Color(style.asphalt.aggregateColorHex) },
    uAggregateIntensity: { value: style.asphalt.aggregateIntensity },
    uGrainTileMeters: { value: style.asphalt.grainTileMeters },
    uGrainDetileMix: { value: style.asphalt.grainDetileMix },
    uEdgeLineColor: { value: new THREE.Color(style.edgeLines.colorHex) },
    uEdgeLineHalfWidth: { value: style.edgeLines.width * 0.5 },
    uEdgeLineShoulderMeters: { value: EDGE_LINE_ASPHALT_SHOULDER },
    uEdgeLineOpacity: { value: THREE.MathUtils.clamp(0.72 + style.edgeLines.emissiveStrength * 0.24, 0, 1) },
    uRubberColor: { value: new THREE.Color(style.asphalt.rubberStreakHex) },
    uRubberIntensity: { value: style.asphalt.rubberStreakIntensity },
    uRubberLaneWidth: { value: style.asphalt.rubberStreakLaneWidth },
    uOilColor: { value: new THREE.Color(style.asphalt.oilSheenHex) },
    uOilIntensity: { value: style.asphalt.oilSheenIntensity },
    uTrackGlintColor: { value: new THREE.Color(style.trackGlint.colorHex) },
    uTrackGlintIntensity: { value: style.trackGlint.intensity },
    uTrackGlintExponent: { value: style.trackGlint.exponent },
    uTrackGlintStreakScale: { value: style.trackGlint.streakScale },
    uTrackGlintBroadness: { value: style.trackGlint.broadness },
    uTrackGlintTextureInfluence: { value: style.trackGlint.textureInfluence },
    uTrackBumpStrength: { value: style.trackGlint.normalStrength },
    uTrackRoughnessContrast: { value: style.trackGlint.roughnessContrast },
    uTrackGlintSunDir: {
      value: new THREE.Vector3(style.sky.sunDir.x, style.sky.sunDir.y, style.sky.sunDir.z).normalize(),
    },
  };
}

interface AsphaltFastUniforms {
  uAsphaltBase: { value: THREE.Color };
  uAggregateColor: { value: THREE.Color };
  uAggregateIntensity: { value: number };
  uGrainTileMeters: { value: number };
  uEdgeLineColor: { value: THREE.Color };
  uEdgeLineHalfWidth: { value: number };
  uEdgeLineShoulderMeters: { value: number };
  uEdgeLineOpacity: { value: number };
}

function installFastAsphaltShader(material: THREE.MeshStandardMaterial, style: StyleConfig): void {
  const uniforms = makeAsphaltFastUniforms(style);
  material.userData.asphaltFastShaderUniforms = uniforms;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace(
        'void main() {',
        `
attribute float aTrackHalfWidth;
varying vec2 vFastTrackUv;
varying float vFastTrackHalfWidth;

void main() {
        `.trim(),
      )
      .replace(
        '#include <uv_vertex>',
        `
#include <uv_vertex>
vFastTrackUv = uv;
vFastTrackHalfWidth = aTrackHalfWidth;
        `.trim(),
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        `
uniform vec3 uEdgeLineColor;
uniform vec3 uAsphaltBase;
uniform vec3 uAggregateColor;
uniform float uAggregateIntensity;
uniform float uGrainTileMeters;
uniform float uEdgeLineHalfWidth;
uniform float uEdgeLineShoulderMeters;
uniform float uEdgeLineOpacity;
varying vec2 vFastTrackUv;
varying float vFastTrackHalfWidth;

float fastEdgeLineMask(vec2 uv, float halfWidthMeters) {
  float fullWidth = max(0.001, halfWidthMeters * 2.0);
  float halfLineUv = clamp(uEdgeLineHalfWidth / fullWidth, 0.0, 0.25);
  float centerOffsetUv = clamp((uEdgeLineShoulderMeters + uEdgeLineHalfWidth) / fullWidth, halfLineUv, 0.5);
  float leftCenter = centerOffsetUv;
  float rightCenter = 1.0 - centerOffsetUv;
  float aa = max(fwidth(uv.x) * 1.5, 0.001);
  float left = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(uv.x - leftCenter));
  float right = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(uv.x - rightCenter));
  return max(left, right);
}

void main() {
        `.trim(),
      )
      .replace(
        '#include <map_fragment>',
        `
float grainTile = max(0.5, uGrainTileMeters);
vec2 grainUv = vec2(
  (vFastTrackUv.x - 0.5) * 2.0 * vFastTrackHalfWidth / grainTile,
  vFastTrackUv.y * 6.0 / grainTile
);
vec3 surface = uAsphaltBase;
#ifdef USE_MAP
float grain = (texture2D(map, grainUv).r - 0.5) * 2.0;
surface = mix(
  uAsphaltBase,
  uAggregateColor,
  clamp(0.5 + grain * uAggregateIntensity, 0.0, 1.0)
);
#endif
float edgeMask = fastEdgeLineMask(vFastTrackUv, vFastTrackHalfWidth);
diffuseColor.rgb = mix(surface, uEdgeLineColor, edgeMask * uEdgeLineOpacity);
        `.trim(),
      );
  };
  const prevCacheKey = material.customProgramCacheKey?.bind(material);
  material.customProgramCacheKey = () => `${prevCacheKey ? prevCacheKey() : ''}|asphalt-fast-v1`;
}

function installAsphaltShader(material: THREE.MeshStandardMaterial, track: Track, style: StyleConfig): void {
  const uniforms = makeAsphaltShaderUniforms(track, style);
  material.userData.asphaltShaderUniforms = uniforms;
  // Backwards-compatible alias for tests / callers that still look up the
  // glint subset by name.
  material.userData.trackGlintUniforms = uniforms;
  // `#include <...>` directives are not expanded inside shader.fragmentShader
  // at this point. Replacing an include directive is safe; replacing code from
  // inside a chunk, such as RE_Direct(...), silently no-ops unless you splice
  // in THREE.ShaderChunk content yourself first.
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace(
        'void main() {',
        `
attribute float aRacingLineU;
attribute float aTrackHalfWidth;
varying vec3 vTrackGlintWorldPosition;
varying vec3 vTrackGlintWorldNormal;
varying vec2 vTrackGlintUv;
varying float vRacingLineU;
varying float vTrackHalfWidth;

void main() {
        `.trim(),
      )
      .replace(
        '#include <defaultnormal_vertex>',
        `
#include <defaultnormal_vertex>
vTrackGlintWorldNormal = normalize(transformedNormal);
        `.trim(),
      )
      .replace(
        '#include <uv_vertex>',
        `
#include <uv_vertex>
vTrackGlintUv = uv;
vRacingLineU = aRacingLineU;
vTrackHalfWidth = aTrackHalfWidth;
        `.trim(),
      )
      .replace(
        '#include <worldpos_vertex>',
        `
#include <worldpos_vertex>
vTrackGlintWorldPosition = worldPosition.xyz;
        `.trim(),
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        `
uniform vec3 uAsphaltBase;
uniform vec3 uAggregateColor;
uniform float uAggregateIntensity;
uniform float uGrainTileMeters;
uniform float uGrainDetileMix;
uniform vec3 uEdgeLineColor;
uniform float uEdgeLineHalfWidth;
uniform float uEdgeLineShoulderMeters;
uniform float uEdgeLineOpacity;
uniform vec3 uRubberColor;
uniform float uRubberIntensity;
uniform float uRubberLongScale;
uniform float uRubberLateralFreq;
uniform float uRubberLaneWidth;
uniform float uRubberThreshold;
uniform vec3 uOilColor;
uniform float uOilIntensity;
uniform float uOilLongScale;
uniform float uOilThreshold;
uniform vec3 uTrackGlintColor;
uniform float uTrackGlintIntensity;
uniform float uTrackGlintExponent;
uniform float uTrackGlintStreakScale;
uniform float uTrackGlintBroadness;
uniform float uTrackGlintTextureInfluence;
uniform float uTrackBumpStrength;
uniform float uTrackRoughnessContrast;
uniform vec3 uTrackGlintSunDir;
varying vec3 vTrackGlintWorldPosition;
varying vec3 vTrackGlintWorldNormal;
varying vec2 vTrackGlintUv;
varying float vRacingLineU;
varying float vTrackHalfWidth;

float trackGlintHash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float trackGlintNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = trackGlintHash(i);
  float b = trackGlintHash(i + vec2(1.0, 0.0));
  float c = trackGlintHash(i + vec2(0.0, 1.0));
  float d = trackGlintHash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float trackGlintFbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * trackGlintNoise(p);
    p *= 2.03;
    a *= 0.5;
  }
  return v;
}

float edgeLineMask(vec2 uv, float halfWidthMeters) {
  float fullWidth = max(0.001, halfWidthMeters * 2.0);
  float halfLineUv = clamp(uEdgeLineHalfWidth / fullWidth, 0.0, 0.25);
  float centerOffsetUv = clamp((uEdgeLineShoulderMeters + uEdgeLineHalfWidth) / fullWidth, halfLineUv, 0.5);
  float leftCenter = centerOffsetUv;
  float rightCenter = 1.0 - centerOffsetUv;
  float aa = max(fwidth(uv.x) * 1.5, 0.001);
  float left = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(uv.x - leftCenter));
  float right = 1.0 - smoothstep(halfLineUv, halfLineUv + aa, abs(uv.x - rightCenter));
  return max(left, right);
}

vec2 asphaltTrackMeters(vec2 uv) {
  return vec2(uv.y * 6.0, (0.5 - uv.x) * 2.0 * vTrackHalfWidth);
}

float asphaltMicroHeight(vec2 p) {
  float grit = 0.5;
#ifdef USE_MAP
  // Match the diffuse grain's meter-scaled lateral UV so micro-bumps run
  // with the noise instead of being stretched across the corridor.
  vec2 gritUv = vec2((p.x - 0.5) * 2.0 * vTrackHalfWidth / 6.0, p.y);
  grit = texture2D(map, gritUv).r;
#endif
  float laneFold = abs(p.x - vRacingLineU);
  float longitudinal = trackGlintFbm(vec2(laneFold * 210.0 + 4.7, p.y * 18.0));
  float fineGroove = trackGlintFbm(vec2(laneFold * 92.0, p.y * 36.0 + longitudinal * 3.0));
  float lane = 1.0 - smoothstep(0.0, uRubberLaneWidth * 1.8, laneFold);
  return (grit - 0.5) * 0.62
    + (longitudinal - 0.5) * 0.34
    + (fineGroove - 0.5) * 0.18
    - lane * 0.10;
}

vec3 asphaltDirectGlint(vec3 lightColor, vec3 lightDirView, vec3 normalView, vec3 viewDirView, float oilMask) {
  vec3 sunDirView = normalize((viewMatrix * vec4(normalize(uTrackGlintSunDir), 0.0)).xyz);
  float sunLightAlignment = smoothstep(0.58, 0.96, abs(dot(normalize(lightDirView), sunDirView)));
  vec3 trackGlintReflection = reflect(-normalize(lightDirView), normalize(normalView));
  float trackGlintSpec = max(0.0, dot(normalize(viewDirView), trackGlintReflection));
  float trackGlintTight = pow(trackGlintSpec, max(1.0, uTrackGlintExponent));
  float trackGlintBroadPower = mix(max(2.0, uTrackGlintExponent * 0.32), 2.2, uTrackGlintBroadness);
  float trackGlintBroad = pow(trackGlintSpec, trackGlintBroadPower) * uTrackGlintBroadness * 0.72;
  float trackGlintGrazing = pow(1.0 - max(0.0, dot(normalize(viewDirView), normalize(normalView))), 1.65) * uTrackGlintBroadness * 0.28;
  float trackGlintSunFacing = smoothstep(-0.15, 0.35, dot(normalize(normalView), normalize(lightDirView)));
  float trackGlintAcross = abs(vTrackGlintUv.x - 0.5) * 2.0;
  float trackGlintLane = 1.0 - smoothstep(0.42, 1.0, trackGlintAcross);
  float trackGlintFold = abs(vTrackGlintUv.x - vRacingLineU);
  float trackGlintStreak = trackGlintFbm(vec2(trackGlintFold * 3.2, vTrackGlintUv.y * uTrackGlintStreakScale * 0.16));
  trackGlintStreak = smoothstep(0.18, 0.92, trackGlintStreak);
  float trackGlintSheen = uTrackGlintBroadness * (0.05 + trackGlintStreak * 0.32);
  float rubberLane = 1.0 - smoothstep(0.0, uRubberLaneWidth, trackGlintFold);
  float textureResponse = clamp(
    0.42 + trackGlintStreak * 1.08 + oilMask * 1.85 - rubberLane * uRubberIntensity * 0.34,
    0.08,
    2.35
  );
  float trackGlintAmount = (trackGlintTight + trackGlintBroad + trackGlintGrazing + trackGlintSheen)
    * trackGlintSunFacing
    * sunLightAlignment
    * trackGlintLane
    * mix(1.0, textureResponse, uTrackGlintTextureInfluence);
  return uTrackGlintColor * lightColor * uTrackGlintIntensity * trackGlintAmount;
}

void main() {
        `.trim(),
      )
      .replace(
        '#include <map_fragment>',
        `
float asphaltOilMask = 0.0;
// Meter-scaled UV for the procedural grain. vTrackGlintUv.x is normalized
// across the full corridor (~17 m); vTrackGlintUv.y is arc / 6 m. Recenter
// U to [-1,1], scale to metres via vTrackHalfWidth, then divide both axes
// by uGrainTileMeters so noise samples isotropically and tile period is
// configurable from the panel.
float grainTile = max(0.5, uGrainTileMeters);
vec2 grainUv = vec2(
  (vTrackGlintUv.x - 0.5) * 2.0 * vTrackHalfWidth / grainTile,
  vTrackGlintUv.y * 6.0 / grainTile
);
#ifdef USE_MAP
  vec4 sampledDiffuseColor = texture2D(map, grainUv);
  // Detile: a second sample at an irrational scale + offset, blended in,
  // breaks the visible repeat at the cost of contrast. The DataTexture is
  // grayscale grit centered on 0.5: positive deltas push toward the
  // aggregate color, negative deltas hold the base.
  vec2 grainUvDetile = grainUv * 0.4137 + vec2(31.7, 17.3);
  float grainDetileSample = texture2D(map, grainUvDetile).r;
  float grainPrimary = (sampledDiffuseColor.r - 0.5);
  float grainSecondary = (grainDetileSample - 0.5);
  float grain = (grainPrimary + grainSecondary * uGrainDetileMix)
    * 2.0 / (1.0 + uGrainDetileMix);
  vec3 surface = mix(
    uAsphaltBase,
    uAggregateColor,
    clamp(0.5 + grain * uAggregateIntensity, 0.0, 1.0)
  );

  float edgeMask = edgeLineMask(vTrackGlintUv, vTrackHalfWidth);
  surface = mix(surface, uEdgeLineColor, edgeMask * uEdgeLineOpacity);

  float laneDist = abs(vTrackGlintUv.x - vRacingLineU);

  // Rubber streaks: long anisotropic strokes confined to a band around the
  // racing line. Lateral freq compresses noise across the road; longScale
  // stretches it along the tangent so streaks read as buildup, not stipple.
  float laneMaskRubber = 1.0 - smoothstep(0.0, uRubberLaneWidth, laneDist);
  float rubberFbm = trackGlintFbm(vec2(
    laneDist * uRubberLateralFreq,
    vTrackGlintUv.y * uRubberLongScale + 11.3
  ));
  float rubber = smoothstep(uRubberThreshold, 1.0, rubberFbm) * laneMaskRubber * uRubberIntensity;
  surface = mix(surface, uRubberColor, rubber);

  // Oil sheen: wider band, view-dependent fresnel boost so it pops at low
  // angles like a true wet/oily film. oilViewDir is reused later in the
  // colorspace_fragment glint block, so declare here and read there.
  vec3 oilViewDir = normalize(cameraPosition - vTrackGlintWorldPosition);
  float laneMaskOil = 1.0 - smoothstep(0.0, uRubberLaneWidth * 1.6, laneDist);
  float oilFbm = trackGlintFbm(vec2(
    laneDist * 5.0 + 47.7,
    vTrackGlintUv.y * uOilLongScale + 73.1
  ));
  float fresnel = pow(1.0 - max(0.0, oilViewDir.y), 3.0);
  float oil = smoothstep(uOilThreshold, 1.0, oilFbm) * laneMaskOil * uOilIntensity * (0.4 + fresnel * 0.6);
  asphaltOilMask = oil;
  surface = mix(surface, uOilColor, oil);

  diffuseColor.rgb = surface;
#endif
        `.trim(),
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `
#include <roughnessmap_fragment>
float asphaltRoughnessLaneFold = abs(vTrackGlintUv.x - vRacingLineU);
float asphaltRoughnessNoise = trackGlintFbm(vec2(asphaltRoughnessLaneFold * 9.0, vTrackGlintUv.y * 74.0));
roughnessFactor = clamp(
  roughnessFactor
    + (asphaltRoughnessNoise - 0.5) * 0.08 * uTrackRoughnessContrast
    - asphaltOilMask * 0.32 * uTrackRoughnessContrast,
  0.26,
  0.96
);
        `.trim(),
      )
      .replace(
        '#include <normal_fragment_maps>',
        `
#include <normal_fragment_maps>
vec2 asphaltBumpStep = vec2(0.0022, 0.0009);
float asphaltHeightL = asphaltMicroHeight(vTrackGlintUv - vec2(asphaltBumpStep.x, 0.0));
float asphaltHeightR = asphaltMicroHeight(vTrackGlintUv + vec2(asphaltBumpStep.x, 0.0));
float asphaltHeightD = asphaltMicroHeight(vTrackGlintUv - vec2(0.0, asphaltBumpStep.y));
float asphaltHeightU = asphaltMicroHeight(vTrackGlintUv + vec2(0.0, asphaltBumpStep.y));
vec2 asphaltHeightGradient = vec2(asphaltHeightR - asphaltHeightL, asphaltHeightU - asphaltHeightD);

vec3 asphaltQ1 = dFdx(vTrackGlintWorldPosition);
vec3 asphaltQ2 = dFdy(vTrackGlintWorldPosition);
vec2 asphaltSt1 = dFdx(vTrackGlintUv);
vec2 asphaltSt2 = dFdy(vTrackGlintUv);
vec3 asphaltTangent = normalize(asphaltQ1 * asphaltSt2.t - asphaltQ2 * asphaltSt1.t);
vec3 asphaltBitangent = normalize(-asphaltQ1 * asphaltSt2.s + asphaltQ2 * asphaltSt1.s);
float asphaltBumpStrength = uTrackBumpStrength * (0.35 + uAggregateIntensity * 0.72 + asphaltOilMask * 0.34);
vec3 asphaltWorldNormal = normalize(
  vTrackGlintWorldNormal
  - asphaltTangent * asphaltHeightGradient.x * asphaltBumpStrength
  - asphaltBitangent * asphaltHeightGradient.y * asphaltBumpStrength
);
normal = normalize((viewMatrix * vec4(asphaltWorldNormal, 0.0)).xyz);
        `.trim(),
      )
      // `#include <lights_fragment_begin>` is not expanded by Three until
      // after `onBeforeCompile` returns, so a replace targeting the inlined
      // `RE_Direct( directLight, ... )` lines runs against text that doesn't
      // exist yet and silently no-ops. Pre-expand the chunk here, inject the
      // glint into each direct-light call, then substitute the rewritten
      // chunk for the include directive.
      .replace(
        '#include <lights_fragment_begin>',
        THREE.ShaderChunk.lights_fragment_begin.replaceAll(
          'RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );',
          `
RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
reflectedLight.directSpecular += asphaltDirectGlint(directLight.color, directLight.direction, geometryNormal, geometryViewDir, asphaltOilMask);
          `.trim(),
        ),
      );
  };
}

function makeAsphaltShaderUniforms(track: Track, style: StyleConfig): AsphaltShaderUniforms {
  const crackUniforms = makeSparseCrackUniforms(track, style);
  return {
    uAsphaltBase: { value: new THREE.Color(style.asphalt.baseHex) },
    uAggregateColor: { value: new THREE.Color(style.asphalt.aggregateColorHex) },
    uAggregateIntensity: { value: style.asphalt.aggregateIntensity },
    uGrainTileMeters: { value: style.asphalt.grainTileMeters },
    uGrainDetileMix: { value: style.asphalt.grainDetileMix },
    uEdgeLineColor: { value: new THREE.Color(style.edgeLines.colorHex) },
    uEdgeLineHalfWidth: { value: style.edgeLines.width * 0.5 },
    uEdgeLineShoulderMeters: { value: EDGE_LINE_ASPHALT_SHOULDER },
    uEdgeLineOpacity: { value: THREE.MathUtils.clamp(0.72 + style.edgeLines.emissiveStrength * 0.24, 0, 1) },
    uRubberColor: { value: new THREE.Color(style.asphalt.rubberStreakHex) },
    uRubberIntensity: { value: style.asphalt.rubberStreakIntensity },
    uRubberLongScale: { value: style.asphalt.rubberStreakLongScale },
    uRubberLateralFreq: { value: style.asphalt.rubberStreakLateralFreq },
    uRubberLaneWidth: { value: style.asphalt.rubberStreakLaneWidth },
    uRubberThreshold: { value: style.asphalt.rubberStreakThreshold },
    uOilColor: { value: new THREE.Color(style.asphalt.oilSheenHex) },
    uOilIntensity: { value: style.asphalt.oilSheenIntensity },
    uOilLongScale: { value: style.asphalt.oilSheenLongScale },
    uOilThreshold: { value: style.asphalt.oilSheenThreshold },
    uCrackColor: { value: new THREE.Color(style.asphalt.cracks.colorHex) },
    uCrackIntensity: { value: style.asphalt.cracks.intensity },
    uCrackFineChecking: { value: style.asphalt.cracks.fineChecking },
    uCrackDepth: { value: style.asphalt.cracks.depth },
    uCrackSegmentCount: { value: crackUniforms.count },
    uCrackSegments: { value: crackUniforms.segments },
    uCrackParams: { value: crackUniforms.params },
    uTrackGlintColor: { value: new THREE.Color(style.trackGlint.colorHex) },
    uTrackGlintIntensity: { value: style.trackGlint.intensity },
    uTrackGlintExponent: { value: style.trackGlint.exponent },
    uTrackGlintStreakScale: { value: style.trackGlint.streakScale },
    uTrackGlintBroadness: { value: style.trackGlint.broadness },
    uTrackGlintTextureInfluence: { value: style.trackGlint.textureInfluence },
    uTrackBumpStrength: { value: style.trackGlint.normalStrength },
    uTrackRoughnessContrast: { value: style.trackGlint.roughnessContrast },
    uTrackGlintSunDir: {
      value: new THREE.Vector3(style.sky.sunDir.x, style.sky.sunDir.y, style.sky.sunDir.z).normalize(),
    },
  };
}

function makeAsphaltFastUniforms(style: StyleConfig): AsphaltFastUniforms {
  return {
    uAsphaltBase: { value: new THREE.Color(style.asphalt.baseHex) },
    uAggregateColor: { value: new THREE.Color(style.asphalt.aggregateColorHex) },
    uAggregateIntensity: { value: style.asphalt.aggregateIntensity },
    uGrainTileMeters: { value: style.asphalt.grainTileMeters },
    uEdgeLineColor: { value: new THREE.Color(style.edgeLines.colorHex) },
    uEdgeLineHalfWidth: { value: style.edgeLines.width * 0.5 },
    uEdgeLineShoulderMeters: { value: EDGE_LINE_ASPHALT_SHOULDER },
    uEdgeLineOpacity: { value: THREE.MathUtils.clamp(0.72 + style.edgeLines.emissiveStrength * 0.24, 0, 1) },
  };
}

interface SparseCrackUniforms {
  count: number;
  segments: THREE.Vector4[];
  params: THREE.Vector4[];
}

interface SparseCrackSegment {
  familyIndex: number;
  startS: number;
  startU: number;
  endS: number;
  endU: number;
  width: number;
  depth: number;
  strength: number;
  lipSide: number;
}

interface CrackPolylineStart {
  familyIndex: number;
  s: number;
  u: number;
  angle: number;
  length: number;
  width: number;
  depth: number;
  strength: number;
  lipSide: number;
}

function makeSparseCrackUniforms(track: Track, style: StyleConfig): SparseCrackUniforms {
  const segments = Array.from({ length: ASPHALT_MAX_CRACK_SEGMENTS }, () => new THREE.Vector4());
  const params = Array.from({ length: ASPHALT_MAX_CRACK_SEGMENTS }, () => new THREE.Vector4());
  const cracks = generateSparseCrackSegments(track, style);
  for (let i = 0; i < Math.min(cracks.length, ASPHALT_MAX_CRACK_SEGMENTS); i++) {
    const crack = cracks[i];
    segments[i].set(crack.startS, crack.startU, crack.endS, crack.endU);
    params[i].set(crack.width, crack.depth, crack.strength, crack.lipSide);
  }
  return { count: Math.min(cracks.length, ASPHALT_MAX_CRACK_SEGMENTS), segments, params };
}

function updateSparseCrackUniforms(uniforms: AsphaltShaderUniforms, track: Track, style: StyleConfig): void {
  const next = generateSparseCrackSegments(track, style);
  const count = Math.min(next.length, ASPHALT_MAX_CRACK_SEGMENTS);
  uniforms.uCrackSegmentCount.value = count;
  for (let i = 0; i < ASPHALT_MAX_CRACK_SEGMENTS; i++) {
    const crack = next[i];
    if (i < count && crack) {
      uniforms.uCrackSegments.value[i].set(crack.startS, crack.startU, crack.endS, crack.endU);
      uniforms.uCrackParams.value[i].set(crack.width, crack.depth, crack.strength, crack.lipSide);
    } else {
      uniforms.uCrackSegments.value[i].set(0, 0, 0, 0);
      uniforms.uCrackParams.value[i].set(0, 0, 0, 0);
    }
  }
}

function buildCrackRelief(track: Track, style: StyleConfig): THREE.Group {
  void track;
  void style;
  const group = new THREE.Group();
  group.name = 'track-crack-relief';
  group.userData.renderedByAsphaltShader = true;
  return group;
}

function rebuildCrackRelief(group: THREE.Group, track: Track, style: StyleConfig): void {
  void track;
  void style;
  disposeObjectChildren(group);
  group.userData.renderedByAsphaltShader = true;
  tagValueBucket(group, 'mg');
}

function buildCrackDecals(
  track: Track,
  style: StyleConfig,
  bakedCracks: BakedCrackDecals | null,
  rendererMode: RendererMode,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'track-crack-decals';
  if (bakedCracks && bakedCracks.decals.length > 0) addBakedCrackDecalMeshes(group, bakedCracks, rendererMode);
  else addCrackDecalMeshes(group, track, style, rendererMode);
  return group;
}

function rebuildCrackDecals(
  group: THREE.Group,
  track: Track,
  style: StyleConfig,
  bakedCracks: BakedCrackDecals | null,
  rendererMode: RendererMode,
): void {
  disposeObjectChildren(group);
  if (bakedCracks && bakedCracks.decals.length > 0) addBakedCrackDecalMeshes(group, bakedCracks, rendererMode);
  else addCrackDecalMeshes(group, track, style, rendererMode);
  tagValueBucket(group, 'mg');
}

function addBakedCrackDecalMeshes(group: THREE.Group, bakedCracks: BakedCrackDecals, rendererMode: RendererMode): void {
  const texture = new THREE.TextureLoader().load(bakedCracks.atlasUrl);
  texture.name = 'track-crack-baked-atlas';
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.flipY = false;
  group.userData.source = 'baked';
  group.userData.atlasUrl = bakedCracks.atlasUrl;

  for (const decal of bakedCracks.decals) {
    if (decal.positions.length !== 12 || decal.uv.length !== 8) continue;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(decal.positions), 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(decal.uv), 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.computeVertexNormals();
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: texture,
      transparent: true,
      opacity: THREE.MathUtils.clamp(decal.opacity, 0, 1),
      depthWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -3,
      polygonOffsetUnits: -3,
    });
    installValueComposition(material, 'mg', rendererMode);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = decal.name;
    mesh.renderOrder = decal.renderOrder ?? 6;
    if (decal.userData) Object.assign(mesh.userData, decal.userData);
    mesh.userData.bakedCrackDecal = true;
    tagValueBucket(mesh, 'mg');
    group.add(mesh);
  }
}

function addCrackDecalMeshes(group: THREE.Group, track: Track, style: StyleConfig, rendererMode: RendererMode): void {
  const cracks = generateSparseCrackSegments(track, style);
  for (let i = 0; i < cracks.length; i++) {
    const crack = cracks[i];
    const visible = THREE.MathUtils.clamp(style.asphalt.cracks.intensity * crack.strength, 0, 1);
    if (visible <= 0.02) continue;
    addCrackFractureMesh(group, track, crack, i, visible, style, rendererMode);
  }
}

function addCrackFractureMesh(
  group: THREE.Group,
  track: Track,
  crack: SparseCrackSegment,
  index: number,
  opacity: number,
  style: StyleConfig,
  rendererMode: RendererMode,
): void {
  const planeWidth = THREE.MathUtils.clamp(crack.width * 3.6, 0.78, 1.75);
  const half = planeWidth * 0.5;
  const yOffset = 0.026;
  const positions = new Float32Array(12);
  const points = [
    crackWorldPoint(track, crack.startS, crack.startU - half, yOffset),
    crackWorldPoint(track, crack.startS, crack.startU + half, yOffset),
    crackWorldPoint(track, crack.endS, crack.endU + half, yOffset),
    crackWorldPoint(track, crack.endS, crack.endU - half, yOffset),
  ];
  for (let i = 0; i < points.length; i++) {
    positions[i * 3 + 0] = points[i].x;
    positions[i * 3 + 1] = points[i].y;
    positions[i * 3 + 2] = points[i].z;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.computeVertexNormals();
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([
    0, 0,
    1, 0,
    1, 1,
    0, 1,
  ]), 2));
  const texture = makeCrackFractureTexture(crack, index, style);
  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(0xffffff),
    map: texture,
    transparent: true,
    opacity: THREE.MathUtils.clamp(opacity * 1.08, 0.28, 1),
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  });
  installValueComposition(material, 'mg', rendererMode);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = `track-crack-fracture-${index}`;
  mesh.renderOrder = 6;
  mesh.userData.fracturePlaneWidthMeters = planeWidth;
  mesh.userData.crackFamilyIndex = crack.familyIndex;
  mesh.userData.crackStartS = crack.startS;
  mesh.userData.crackEndS = crack.endS;
  mesh.userData.crackStartU = crack.startU;
  mesh.userData.crackEndU = crack.endU;
  tagValueBucket(mesh, 'mg');
  group.add(mesh);
}

function makeCrackFractureTexture(
  crack: SparseCrackSegment,
  index: number,
  style: StyleConfig,
): THREE.DataTexture {
  const width = 72;
  const height = 224;
  const data = new Uint8Array(width * height * 4);
  const rng = makeCrackRandom(
    style.asphalt.cracks.seed
      + index * 7919
      + Math.floor(crack.startS * 17)
      + Math.floor((crack.startU + 16) * 97),
  );
  const crackColor = new THREE.Color(style.asphalt.cracks.colorHex);
  const edgeColor = new THREE.Color(style.asphalt.aggregateColorHex).lerp(crackColor, 0.72);
  const centerXs = new Float32Array(height);
  let center = width * 0.5 + (rng() - 0.5) * width * 0.08;
  let branchPixels = 0;

  for (let y = 0; y < height; y++) {
    const t = y / Math.max(1, height - 1);
    center += (rng() - 0.5) * 2.25 + Math.sin(t * Math.PI * 9.0 + index) * 0.18;
    center = THREE.MathUtils.clamp(center, width * 0.28, width * 0.72);
    centerXs[y] = center;
    const taper = Math.sin(t * Math.PI);
    const radius = THREE.MathUtils.clamp(0.65 + rng() * 1.35 + taper * 0.72, 0.65, 2.75);
    for (let x = Math.floor(center - 5); x <= Math.ceil(center + 5); x++) {
      const d = Math.abs(x - center + (rng() - 0.5) * 0.38);
      if (d <= radius) {
        const core = d < Math.max(0.55, radius * 0.42);
        const alpha = Math.round((core ? 205 + rng() * 50 : 82 + rng() * 68) * THREE.MathUtils.clamp(taper * 1.35, 0.25, 1));
        writeCrackPixel(data, width, height, x, y, crackColor, alpha);
      } else if (d <= radius + 1.05 && rng() > 0.5) {
        const alpha = Math.round((12 + rng() * 6) * THREE.MathUtils.clamp(taper * 1.2, 0.18, 1));
        writeCrackPixel(data, width, height, x, y, edgeColor, alpha);
      }
    }
  }

  const branchCount = THREE.MathUtils.clamp(Math.round(style.asphalt.cracks.branchiness * 3 + rng() * 2), 1, 4);
  for (let b = 0; b < branchCount; b++) {
    const startY = Math.floor(THREE.MathUtils.lerp(height * 0.18, height * 0.82, rng()));
    let x = centerXs[startY];
    let y = startY;
    const direction = rng() < 0.5 ? -1 : 1;
    const length = Math.floor(THREE.MathUtils.lerp(18, 46, rng()));
    const slope = direction * THREE.MathUtils.lerp(0.32, 0.74, rng());
    for (let step = 0; step < length; step++) {
      x += slope + (rng() - 0.5) * 0.95;
      y += rng() < 0.18 ? 0 : (rng() < 0.5 ? -1 : 1);
      const fade = 1 - step / length;
      const px = Math.round(x);
      const py = Math.round(y);
      writeCrackPixel(data, width, height, px, py, crackColor, Math.round(150 * fade));
      branchPixels += 1;
      if (rng() > 0.58 && fade > 0.25) {
        writeCrackPixel(data, width, height, px + direction, py, crackColor, Math.round(62 * fade));
        branchPixels += 1;
      }
    }
  }

  limitCrackRunWidth(data, width, height, 9);
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  texture.name = `track-crack-fracture-texture-${index}`;
  texture.needsUpdate = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.userData.crackTextureStats = crackTextureStats(data, width, height, branchPixels);
  return texture;
}

function limitCrackRunWidth(data: Uint8Array, width: number, height: number, maxRun: number): void {
  for (let y = 0; y < height; y++) {
    let runStart = -1;
    for (let x = 0; x <= width; x++) {
      const alpha = x < width ? data[(y * width + x) * 4 + 3] : 0;
      if (alpha > 18 && runStart < 0) runStart = x;
      if ((alpha <= 18 || x === width) && runStart >= 0) {
        const runEnd = x - 1;
        const runLength = runEnd - runStart + 1;
        if (runLength > maxRun) {
          const center = (runStart + runEnd) * 0.5;
          const keepStart = Math.round(center - (maxRun - 1) * 0.5);
          const keepEnd = keepStart + maxRun - 1;
          for (let trimX = runStart; trimX <= runEnd; trimX++) {
            if (trimX >= keepStart && trimX <= keepEnd) continue;
            data[(y * width + trimX) * 4 + 3] = Math.min(data[(y * width + trimX) * 4 + 3], 16);
          }
        }
        runStart = -1;
      }
    }
  }
}

function writeCrackPixel(
  data: Uint8Array,
  width: number,
  height: number,
  x: number,
  y: number,
  color: THREE.Color,
  alpha: number,
): void {
  if (x < 0 || x >= width || y < 0 || y >= height || alpha <= 0) return;
  const offset = (y * width + x) * 4;
  const nextAlpha = THREE.MathUtils.clamp(alpha, 0, 255);
  if (nextAlpha < data[offset + 3]) return;
  data[offset + 0] = Math.round(color.r * 255);
  data[offset + 1] = Math.round(color.g * 255);
  data[offset + 2] = Math.round(color.b * 255);
  data[offset + 3] = nextAlpha;
}

function crackTextureStats(
  data: Uint8Array,
  width: number,
  height: number,
  branchPixels: number,
): {
  alphaCoverage: number;
  darkCoreCoverage: number;
  branchCoverage: number;
  maxRunWidthPx: number;
} {
  let alphaPixels = 0;
  let darkCorePixels = 0;
  let maxRunWidthPx = 0;
  for (let y = 0; y < height; y++) {
    let rowRun = 0;
    for (let x = 0; x < width; x++) {
      const offset = (y * width + x) * 4;
      const alpha = data[offset + 3];
      if (alpha > 18) {
        alphaPixels += 1;
        rowRun += 1;
        if (alpha >= 145) darkCorePixels += 1;
      } else {
        maxRunWidthPx = Math.max(maxRunWidthPx, rowRun);
        rowRun = 0;
      }
    }
    maxRunWidthPx = Math.max(maxRunWidthPx, rowRun);
  }
  const total = width * height;
  return {
    alphaCoverage: alphaPixels / total,
    darkCoreCoverage: darkCorePixels / total,
    branchCoverage: branchPixels / total,
    maxRunWidthPx,
  };
}

function crackWorldPoint(track: Track, s: number, u: number, yOffset: number): THREE.Vector3 {
  const sampleIndex = sampleIndexAtArc(track, s);
  const sample = sampleRenderRibbon(track, sampleIndex);
  const halfWidth = (sample.widthLeft + sample.widthRight) * 0.5;
  const lateral = THREE.MathUtils.clamp(u, -halfWidth + 0.18, halfWidth - 0.18);
  return new THREE.Vector3(
    sample.x + sample.nx * lateral,
    trackSurfaceHeightAtRenderSample(track, sampleIndex, lateral) + TRACK_SURFACE_CLEARANCE + yOffset,
    sample.z + sample.nz * lateral,
  );
}

function generateSparseCrackSegments(track: Track, style: StyleConfig): SparseCrackSegment[] {
  const config = style.asphalt.cracks;
  const totalLength = totalTrackArc(track);
  if (totalLength <= 1) return [];

  const rng = makeCrackRandom(config.seed);
  const segments: SparseCrackSegment[] = [];
  const majorCount = Math.max(0, Math.round(config.majorCount));
  for (let i = 0; i < majorCount && segments.length < ASPHALT_MAX_CRACK_SEGMENTS; i++) {
    const start = crackFamilyStart(track, config, i, rng);
    const written = writeCrackPolyline(
      track,
      segments,
      start,
      rng,
    );

    if (written.length === 0 || config.branchiness <= 0) continue;
    const branchAttempts = Math.floor(config.branchiness * 3 + rng() * 1.4);
    for (let b = 0; b < branchAttempts && segments.length < ASPHALT_MAX_CRACK_SEGMENTS; b++) {
      if (rng() > config.branchiness) continue;
      const base = written[Math.floor(rng() * written.length)];
      const side = rng() < 0.5 ? -1 : 1;
      writeCrackPolyline(
        track,
        segments,
        {
          s: THREE.MathUtils.lerp(base.startS, base.endS, rng()),
          u: THREE.MathUtils.lerp(base.startU, base.endU, rng()),
          angle: start.angle + side * THREE.MathUtils.lerp(0.45, 0.9, rng()),
          length: start.length * THREE.MathUtils.lerp(0.18, 0.42, rng()),
          width: start.width * THREE.MathUtils.lerp(0.32, 0.58, rng()),
          depth: start.depth * 0.55,
          strength: start.strength * 0.65,
          lipSide: -side,
          familyIndex: i,
        },
        rng,
      );
    }
  }
  return segments;
}

function crackFamilyStart(
  track: Track,
  config: StyleConfig['asphalt']['cracks'],
  familyIndex: number,
  rng: () => number,
): CrackPolylineStart {
  const totalLength = totalTrackArc(track);
  const visibleRoadFractions = [0.012, 0.32, 0.58, 0.78];
  if (familyIndex < visibleRoadFractions.length) {
    const sample = sampleRenderRibbon(track, sampleIndexAtArc(track, totalLength * visibleRoadFractions[familyIndex]));
    const halfWidth = (sample.widthLeft + sample.widthRight) * 0.5;
    const lateralTargets = [-1.9, 1.85, -0.65, 2.45];
    const direction = familyIndex % 2 === 0 ? 1 : -1;
    return {
      familyIndex,
      s: THREE.MathUtils.clamp(totalLength * visibleRoadFractions[familyIndex], 1, totalLength - 1),
      u: THREE.MathUtils.clamp(lateralTargets[familyIndex], -halfWidth + 1.1, halfWidth - 1.1),
      angle: direction * THREE.MathUtils.lerp(0.08, 0.34, rng()),
      length: Math.max(14, config.maxLength * THREE.MathUtils.lerp(0.78, 1.05, rng())),
      width: config.width * THREE.MathUtils.lerp(1.0, 1.32, rng()),
      depth: THREE.MathUtils.lerp(0.85, 1.18, rng()),
      strength: THREE.MathUtils.lerp(0.9, 1.0, rng()),
      lipSide: direction,
    };
  }

  const anchor = chooseCrackAnchor(track, rng, config.cornerBias);
  const sample = sampleRenderRibbon(track, anchor.sampleIndex);
  const halfWidth = (sample.widthLeft + sample.widthRight) * 0.5;
  const turn = signedTurnAtSample(track, Math.round(anchor.sampleIndex), 8);
  const familyRoll = rng();
  const edgeFamily = familyRoll < config.edgeBias;
  const transverseFamily = !edgeFamily && familyRoll < config.edgeBias + config.transverseBias;
  const edgeSide = rng() < 0.5 ? -1 : 1;
  const centerJitter = (rng() - 0.5) * halfWidth * 0.55;
  const startU = edgeFamily
    ? edgeSide * (halfWidth - THREE.MathUtils.lerp(0.9, 1.8, rng()))
    : THREE.MathUtils.clamp(centerJitter, -halfWidth + 1.2, halfWidth - 1.2);
  const angle = crackFamilyAngle(rng, turn, edgeFamily, transverseFamily);
  const length = THREE.MathUtils.lerp(config.maxLength * 0.45, config.maxLength, rng());
  return {
    familyIndex,
    s: THREE.MathUtils.clamp(anchor.s - Math.cos(angle) * length * 0.35, 1, totalLength - 1),
    u: startU,
    angle,
    length,
    width: THREE.MathUtils.lerp(config.width * 0.72, config.width * 1.45, rng()),
    depth: THREE.MathUtils.lerp(0.65, 1.2, rng()),
    strength: THREE.MathUtils.lerp(0.72, 1.0, rng()),
    lipSide: rng() < 0.5 ? -1 : 1,
  };
}

function chooseCrackAnchor(
  track: Track,
  rng: () => number,
  cornerBias: number,
): { s: number; sampleIndex: number } {
  const totalLength = totalTrackArc(track);
  let best = { s: rng() * totalLength, sampleIndex: 0, score: -Infinity };
  const candidates = 8;
  for (let i = 0; i < candidates; i++) {
    const s = rng() * totalLength;
    const sampleIndex = sampleIndexAtArc(track, s);
    const curvature = curvatureAt(track, Math.round(sampleIndex));
    const score = rng() + curvature * cornerBias * 2.8;
    if (score > best.score) best = { s, sampleIndex, score };
  }
  return best;
}

function crackFamilyAngle(
  rng: () => number,
  turn: number,
  edgeFamily: boolean,
  transverseFamily: boolean,
): number {
  if (edgeFamily) return (rng() - 0.5) * 0.42;
  if (transverseFamily) return (rng() < 0.5 ? -1 : 1) * THREE.MathUtils.lerp(1.15, 1.48, rng());
  const turnSign = turn === 0 ? (rng() < 0.5 ? -1 : 1) : Math.sign(turn);
  return turnSign * THREE.MathUtils.lerp(0.48, 0.92, rng());
}

function writeCrackPolyline(
  track: Track,
  out: SparseCrackSegment[],
  start: CrackPolylineStart,
  rng: () => number,
): SparseCrackSegment[] {
  const totalLength = totalTrackArc(track);
  const written: SparseCrackSegment[] = [];
  let s = start.s;
  let u = start.u;
  let angle = start.angle;
  let remaining = start.length;
  while (remaining > 0.4 && out.length < ASPHALT_MAX_CRACK_SEGMENTS) {
    const step = Math.min(remaining, THREE.MathUtils.lerp(1.8, 4.2, rng()));
    angle += (rng() - 0.5) * 0.22;
    const nextS = THREE.MathUtils.clamp(s + Math.cos(angle) * step, 1, totalLength - 1);
    const nextSample = sampleRenderRibbon(track, sampleIndexAtArc(track, nextS));
    const nextHalfWidth = (nextSample.widthLeft + nextSample.widthRight) * 0.5;
    const nextU = THREE.MathUtils.clamp(
      u + Math.sin(angle) * step + (rng() - 0.5) * start.width * 1.2,
      -nextHalfWidth + 0.65,
      nextHalfWidth - 0.65,
    );
    if (Math.hypot(nextS - s, nextU - u) < 0.35) break;
    const taper = THREE.MathUtils.clamp(remaining / Math.max(start.length, 0.001), 0.2, 1);
    const segment = {
      familyIndex: start.familyIndex,
      startS: s,
      startU: u,
      endS: nextS,
      endU: nextU,
      width: start.width * THREE.MathUtils.lerp(0.72, 1.12, rng()) * taper,
      depth: start.depth,
      strength: start.strength * taper,
      lipSide: start.lipSide,
    };
    out.push(segment);
    written.push(segment);
    s = nextS;
    u = nextU;
    remaining -= step;
  }
  return written;
}

function makeCrackRandom(seed: number): () => number {
  let state = Math.max(1, Math.floor(seed)) >>> 0;
  return () => {
    state += 0x6D2B79F5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function totalTrackArc(track: Track): number {
  const n = track.centerline.length;
  if (n === 0) return 0;
  return (track.cumulativeLength[n - 1] ?? 0) + segmentLength(track, n - 1, 0);
}

function sampleIndexAtArc(track: Track, arc: number): number {
  const n = track.centerline.length;
  if (n <= 1) return 0;
  const total = totalTrackArc(track);
  const wrapped = ((arc % total) + total) % total;
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi + 1) / 2);
    if ((track.cumulativeLength[mid] ?? 0) <= wrapped) lo = mid;
    else hi = mid - 1;
  }
  const here = track.cumulativeLength[lo] ?? 0;
  const next = lo + 1 < n ? track.cumulativeLength[lo + 1] : total;
  const t = next > here ? (wrapped - here) / (next - here) : 0;
  return lo + t;
}

function applyAsphaltShaderStyle(surface: THREE.Mesh, track: Track, style: StyleConfig): void {
  const mat = surface.material as THREE.MeshStandardMaterial & MeshStandardNodeMaterial;
  const webgpuUniforms = mat.userData.webgpuAsphaltUniforms as WebGPUAsphaltUniforms | undefined;
  if (webgpuUniforms) {
    applyWebGPUAsphaltStyle(surface, mat, webgpuUniforms, style);
    return;
  }
  const uniforms = mat.userData.asphaltShaderUniforms as AsphaltShaderUniforms | undefined;
  const fastUniforms = mat.userData.asphaltFastShaderUniforms as AsphaltFastUniforms | undefined;
  const bakedUniforms = mat.userData.bakedAsphaltUniforms as BakedAsphaltUniforms | undefined;

  // Only regenerate the 512² grit DataTexture when its source field actually
  // changes. Color/intensity tweaks should be one uniform write each.
  const previous = surface.userData.asphaltStyle as StyleConfig['asphalt'] | undefined;
  if (
    !previous
    || previous.aggregateScale !== style.asphalt.aggregateScale
    || previous.grainElongation !== style.asphalt.grainElongation
  ) {
    mat.map?.dispose();
    mat.map = makeProceduralTexture(style);
    mat.needsUpdate = true;
  }

  mat.roughness = style.asphalt.baseRoughness;
  mat.envMapIntensity = style.asphalt.baseEnvIntensity;

  if (bakedUniforms) {
    bakedUniforms.uAsphaltBase.value.set(style.asphalt.baseHex);
    bakedUniforms.uAggregateColor.value.set(style.asphalt.aggregateColorHex);
    bakedUniforms.uAggregateIntensity.value = style.asphalt.aggregateIntensity;
    bakedUniforms.uGrainTileMeters.value = style.asphalt.grainTileMeters;
    bakedUniforms.uGrainDetileMix.value = style.asphalt.grainDetileMix;
    bakedUniforms.uEdgeLineColor.value.set(style.edgeLines.colorHex);
    bakedUniforms.uEdgeLineHalfWidth.value = style.edgeLines.width * 0.5;
    bakedUniforms.uEdgeLineShoulderMeters.value = EDGE_LINE_ASPHALT_SHOULDER;
    bakedUniforms.uEdgeLineOpacity.value = THREE.MathUtils.clamp(0.72 + style.edgeLines.emissiveStrength * 0.24, 0, 1);
    bakedUniforms.uRubberColor.value.set(style.asphalt.rubberStreakHex);
    bakedUniforms.uRubberIntensity.value = style.asphalt.rubberStreakIntensity;
    bakedUniforms.uRubberLaneWidth.value = style.asphalt.rubberStreakLaneWidth;
    bakedUniforms.uOilColor.value.set(style.asphalt.oilSheenHex);
    bakedUniforms.uOilIntensity.value = style.asphalt.oilSheenIntensity;
    bakedUniforms.uTrackGlintColor.value.set(style.trackGlint.colorHex);
    bakedUniforms.uTrackGlintIntensity.value = style.trackGlint.intensity;
    bakedUniforms.uTrackGlintExponent.value = style.trackGlint.exponent;
    bakedUniforms.uTrackGlintStreakScale.value = style.trackGlint.streakScale;
    bakedUniforms.uTrackGlintBroadness.value = style.trackGlint.broadness;
    bakedUniforms.uTrackGlintTextureInfluence.value = style.trackGlint.textureInfluence;
    bakedUniforms.uTrackBumpStrength.value = style.trackGlint.normalStrength;
    bakedUniforms.uTrackRoughnessContrast.value = style.trackGlint.roughnessContrast;
    bakedUniforms.uTrackGlintSunDir.value.set(style.sky.sunDir.x, style.sky.sunDir.y, style.sky.sunDir.z).normalize();
  }

  if (fastUniforms) {
    fastUniforms.uAsphaltBase.value.set(style.asphalt.baseHex);
    fastUniforms.uAggregateColor.value.set(style.asphalt.aggregateColorHex);
    fastUniforms.uAggregateIntensity.value = style.asphalt.aggregateIntensity;
    fastUniforms.uGrainTileMeters.value = style.asphalt.grainTileMeters;
    fastUniforms.uEdgeLineColor.value.set(style.edgeLines.colorHex);
    fastUniforms.uEdgeLineHalfWidth.value = style.edgeLines.width * 0.5;
    fastUniforms.uEdgeLineShoulderMeters.value = EDGE_LINE_ASPHALT_SHOULDER;
    fastUniforms.uEdgeLineOpacity.value = THREE.MathUtils.clamp(0.72 + style.edgeLines.emissiveStrength * 0.24, 0, 1);
  }

  if (!uniforms) {
    surface.userData.asphaltStyle = { ...style.asphalt };
    surface.userData.trackGlintStyle = { ...style.trackGlint };
    return;
  }

  uniforms.uAsphaltBase.value.set(style.asphalt.baseHex);
  uniforms.uAggregateColor.value.set(style.asphalt.aggregateColorHex);
  uniforms.uAggregateIntensity.value = style.asphalt.aggregateIntensity;
  uniforms.uGrainTileMeters.value = style.asphalt.grainTileMeters;
  uniforms.uGrainDetileMix.value = style.asphalt.grainDetileMix;
  uniforms.uEdgeLineColor.value.set(style.edgeLines.colorHex);
  uniforms.uEdgeLineHalfWidth.value = style.edgeLines.width * 0.5;
  uniforms.uEdgeLineShoulderMeters.value = EDGE_LINE_ASPHALT_SHOULDER;
  uniforms.uEdgeLineOpacity.value = THREE.MathUtils.clamp(0.72 + style.edgeLines.emissiveStrength * 0.24, 0, 1);
  uniforms.uRubberColor.value.set(style.asphalt.rubberStreakHex);
  uniforms.uRubberIntensity.value = style.asphalt.rubberStreakIntensity;
  uniforms.uRubberLongScale.value = style.asphalt.rubberStreakLongScale;
  uniforms.uRubberLateralFreq.value = style.asphalt.rubberStreakLateralFreq;
  uniforms.uRubberLaneWidth.value = style.asphalt.rubberStreakLaneWidth;
  uniforms.uRubberThreshold.value = style.asphalt.rubberStreakThreshold;
  uniforms.uOilColor.value.set(style.asphalt.oilSheenHex);
  uniforms.uOilIntensity.value = style.asphalt.oilSheenIntensity;
  uniforms.uOilLongScale.value = style.asphalt.oilSheenLongScale;
  uniforms.uOilThreshold.value = style.asphalt.oilSheenThreshold;
  uniforms.uCrackColor.value.set(style.asphalt.cracks.colorHex);
  uniforms.uCrackIntensity.value = style.asphalt.cracks.intensity;
  uniforms.uCrackFineChecking.value = style.asphalt.cracks.fineChecking;
  uniforms.uCrackDepth.value = style.asphalt.cracks.depth;
  updateSparseCrackUniforms(uniforms, track, style);
  uniforms.uTrackGlintColor.value.set(style.trackGlint.colorHex);
  uniforms.uTrackGlintIntensity.value = style.trackGlint.intensity;
  uniforms.uTrackGlintExponent.value = style.trackGlint.exponent;
  uniforms.uTrackGlintStreakScale.value = style.trackGlint.streakScale;
  uniforms.uTrackGlintBroadness.value = style.trackGlint.broadness;
  uniforms.uTrackGlintTextureInfluence.value = style.trackGlint.textureInfluence;
  uniforms.uTrackBumpStrength.value = style.trackGlint.normalStrength;
  uniforms.uTrackRoughnessContrast.value = style.trackGlint.roughnessContrast;
  uniforms.uTrackGlintSunDir.value.set(style.sky.sunDir.x, style.sky.sunDir.y, style.sky.sunDir.z).normalize();

  surface.userData.asphaltStyle = { ...style.asphalt };
  surface.userData.trackGlintStyle = { ...style.trackGlint };
}

function applyWebGPUAsphaltStyle(
  surface: THREE.Mesh,
  material: MeshStandardNodeMaterial,
  uniforms: WebGPUAsphaltUniforms,
  style: StyleConfig,
): void {
  const previous = surface.userData.asphaltStyle as StyleConfig['asphalt'] | undefined;
  if (
    !previous
    || previous.aggregateScale !== style.asphalt.aggregateScale
    || previous.grainElongation !== style.asphalt.grainElongation
  ) {
    uniforms.gritTexture.value.dispose();
    const gritTexture = makeProceduralTexture(style);
    gritTexture.name = 'webgpu-asphalt-grit';
    uniforms.gritTexture.value = gritTexture;
  }

  setColorVector(uniforms.asphaltBase.value, style.asphalt.baseHex);
  setColorVector(uniforms.aggregateColor.value, style.asphalt.aggregateColorHex);
  // webgpuAggregateMultiplier is WebGPU-only: the WebGL onBeforeCompile path
  // never reads it, so tuning here cannot regress the WebGL look.
  uniforms.aggregateIntensity.value =
    style.asphalt.aggregateIntensity * (style.asphalt.webgpuAggregateMultiplier ?? 1.0);
  uniforms.grainTileMeters.value = style.asphalt.grainTileMeters;
  uniforms.grainDetileMix.value = style.asphalt.grainDetileMix;
  setColorVector(uniforms.edgeLineColor.value, style.edgeLines.colorHex);
  uniforms.edgeLineHalfWidth.value = style.edgeLines.width * 0.5;
  uniforms.edgeLineOpacity.value = THREE.MathUtils.clamp(0.72 + style.edgeLines.emissiveStrength * 0.24, 0, 1);
  setColorVector(uniforms.rubberColor.value, style.asphalt.rubberStreakHex);
  uniforms.rubberIntensity.value = style.asphalt.rubberStreakIntensity;
  uniforms.rubberLongScale.value = style.asphalt.rubberStreakLongScale;
  uniforms.rubberLateralFreq.value = style.asphalt.rubberStreakLateralFreq;
  uniforms.rubberLaneWidth.value = style.asphalt.rubberStreakLaneWidth;
  uniforms.rubberThreshold.value = style.asphalt.rubberStreakThreshold;
  setColorVector(uniforms.oilColor.value, style.asphalt.oilSheenHex);
  uniforms.oilIntensity.value = style.asphalt.oilSheenIntensity;
  uniforms.oilLongScale.value = style.asphalt.oilSheenLongScale;
  uniforms.oilThreshold.value = style.asphalt.oilSheenThreshold;
  setColorVector(uniforms.trackGlintColor.value, style.trackGlint.colorHex);
  uniforms.trackGlintIntensity.value = style.trackGlint.intensity;
  uniforms.trackGlintExponent.value = style.trackGlint.exponent;
  uniforms.trackGlintStreakScale.value = style.trackGlint.streakScale;
  uniforms.trackGlintBroadness.value = style.trackGlint.broadness;
  uniforms.trackGlintTextureInfluence.value = style.trackGlint.textureInfluence;
  // Same WebGPU-only multiplier idea: lets you dial WebGPU bump independently.
  uniforms.trackBumpScale.value =
    style.trackGlint.normalStrength * 0.56 * (style.trackGlint.webgpuBumpMultiplier ?? 1.0);
  uniforms.trackRoughnessContrast.value = style.trackGlint.roughnessContrast;
  uniforms.trackGlintSunDir.value.copy(sunDirectionVector(style));
  uniforms.baseRoughness.value = style.asphalt.baseRoughness;

  material.roughness = style.asphalt.baseRoughness;
  material.envMapIntensity = style.asphalt.baseEnvIntensity;
  surface.userData.asphaltStyle = { ...style.asphalt };
  surface.userData.trackGlintStyle = { ...style.trackGlint };
}

function applySurfaceWearStyle(surface: THREE.Mesh, track: Track, style: StyleConfig): void {
  writeSurfaceWearColors(surface.geometry, track, style);
  const colors = surface.geometry.getAttribute('color');
  colors.needsUpdate = true;
}

function writeSurfaceWearColors(geometry: THREE.BufferGeometry, track: Track, style: StyleConfig): void {
  const colors = geometry.getAttribute('color') as THREE.BufferAttribute;
  const rows = Number(geometry.userData.renderRings) || track.centerline.length;
  const ringsTotal = Number(geometry.userData.renderRingsTotal) || rows;
  const cols = Number(geometry.userData.renderColumns) || colors.count / rows;
  for (let i = 0; i < rows; i++) {
    const sampleIndex = (i / Math.max(1, rows)) * track.centerline.length;
    const arcLen = interpolateTrackArc(track, sampleIndex);
    const bleach = wearFbm(arcLen * 0.012, i * 0.31) - 0.5;
    const racingLineU = racingLineCenterU(track, sampleIndex);
    // Pod racers leave two soot trails (one per engine pod) flanking the
    // racing line. The envelope fades them in and out along arc length so
    // the streaks read as accumulated soot rather than a uniform wash.
    const exhaustEnvelope = exhaustEnvelopeAt(arcLen);
    const exhaustDarkenScale = 0.22 + style.asphalt.centerWear * 0.18;
    for (let j = 0; j < cols; j++) {
      const u = j / (cols - 1);
      const edgeDebris = Math.pow(Math.abs(u - 0.5) * 2, 2.4) * style.asphalt.edgeDebris;
      const leftField = Math.exp(-Math.pow((u - (racingLineU - EXHAUST_POD_OFFSET)) / EXHAUST_STREAK_HALF_WIDTH, 2));
      const rightField = Math.exp(-Math.pow((u - (racingLineU + EXHAUST_POD_OFFSET)) / EXHAUST_STREAK_HALF_WIDTH, 2));
      const exhaustField = Math.max(leftField, rightField);
      const exhaustDarken = THREE.MathUtils.clamp(
        exhaustField * exhaustEnvelope * exhaustDarkenScale,
        0,
        0.5,
      );
      const scalar = clamp01(1 + bleach * 0.05 + edgeDebris - exhaustDarken);
      const index = i * cols + j;
      colors.setXYZ(index, scalar, scalar, scalar);
    }
  }
  // Closing ring (if present) duplicates ring 0's XYZ — give it the same
  // vertex colors so the seam quad doesn't crossfade to black.
  if (ringsTotal > rows) {
    for (let j = 0; j < cols; j++) {
      const src = j;
      const dst = rows * cols + j;
      colors.setXYZ(dst, colors.getX(src), colors.getY(src), colors.getZ(src));
    }
  }
}

const EXHAUST_POD_OFFSET = 0.085;
const EXHAUST_STREAK_HALF_WIDTH = 0.045;

/**
 * Arc-length-driven envelope shared by the asphalt exhaust streaks and any
 * scuff overlays that ride the same wear pattern (edge lines, kerb stripes).
 * Always positive, baseline 0.35 so streaks remain readable; FBM peaks push
 * it up to ~1.5 for "heavy traffic" zones.
 */
function exhaustEnvelopeAt(arcLen: number): number {
  return THREE.MathUtils.clamp(0.45 + (wearFbm(arcLen * 0.16, 1.7) - 0.5) * 1.6, 0.35, 1.5);
}

function racingLineCenterU(track: Track, sampleIndex: number): number {
  const n = track.centerline.length;
  const wrapped = ((sampleIndex % n) + n) % n;
  const i = Math.floor(wrapped);
  const t = wrapped - i;
  if (t === 0) return racingLineCenterUAtSample(track, i);
  const a = racingLineCenterUAtSample(track, i);
  const b = racingLineCenterUAtSample(track, (i + 1) % n);
  return THREE.MathUtils.lerp(a, b, t);
}

function racingLineCenterUAtSample(track: Track, sampleIndex: number): number {
  let turn = 0;
  let weight = 0;
  for (let offset = -10; offset <= 10; offset++) {
    const t = 1 - Math.abs(offset) / 11;
    turn += signedTurnAtSample(track, sampleIndex + offset, 8) * t;
    weight += t;
  }
  const smoothedTurn = THREE.MathUtils.clamp(turn / Math.max(1, weight), -1, 1);
  return THREE.MathUtils.clamp(0.5 - smoothedTurn * 0.34, 0.16, 0.84);
}

export const __test__racingLineCenterU = racingLineCenterU;

function signedTurnAtSample(track: Track, sampleIndex: number, window: number): number {
  const n = track.centerline.length;
  const index = ((sampleIndex % n) + n) % n;
  const prev = track.centerline[(index - window + n) % n];
  const here = track.centerline[index];
  const next = track.centerline[(index + window) % n];
  const ax = here.x - prev.x;
  const az = here.z - prev.z;
  const bx = next.x - here.x;
  const bz = next.z - here.z;
  const alen = Math.hypot(ax, az) || 1;
  const blen = Math.hypot(bx, bz) || 1;
  return (ax * bz - az * bx) / (alen * blen);
}

interface RenderRibbonSample {
  x: number;
  z: number;
  tx: number;
  tz: number;
  nx: number;
  nz: number;
  widthLeft: number;
  widthRight: number;
  arc: number;
}

function sampleRenderRibbon(track: Track, sampleIndex: number): RenderRibbonSample {
  const n = track.centerline.length;
  const wrapped = ((sampleIndex % n) + n) % n;
  const i = Math.floor(wrapped);
  const t = wrapped - i;
  const p0 = track.centerline[(i - 1 + n) % n];
  const p1 = track.centerline[i % n];
  const p2 = track.centerline[(i + 1) % n];
  const p3 = track.centerline[(i + 2) % n];
  const x = catmullRom(p0.x, p1.x, p2.x, p3.x, t);
  const z = catmullRom(p0.z, p1.z, p2.z, p3.z, t);
  const dx = catmullRomDerivative(p0.x, p1.x, p2.x, p3.x, t);
  const dz = catmullRomDerivative(p0.z, p1.z, p2.z, p3.z, t);
  const len = Math.hypot(dx, dz) || 1;
  const tx = dx / len;
  const tz = dz / len;
  return {
    x,
    z,
    tx,
    tz,
    nx: -tz,
    nz: tx,
    widthLeft: Math.max(0.5, catmullRom(p0.widthLeft, p1.widthLeft, p2.widthLeft, p3.widthLeft, t)),
    widthRight: Math.max(0.5, catmullRom(p0.widthRight, p1.widthRight, p2.widthRight, p3.widthRight, t)),
    arc: interpolateTrackArc(track, wrapped),
  };
}

function trackSurfaceHeightAtRenderSample(track: Track, sampleIndex: number, lateral: number): number {
  const n = track.centerline.length;
  const wrapped = ((sampleIndex % n) + n) % n;
  const i = Math.floor(wrapped);
  const t = wrapped - i;
  const a = trackSurfaceHeightAtSample(track, i % n, lateral);
  const b = trackSurfaceHeightAtSample(track, (i + 1) % n, lateral);
  return THREE.MathUtils.lerp(a, b, t);
}

function interpolateTrackArc(track: Track, sampleIndex: number): number {
  const n = track.centerline.length;
  const wrapped = ((sampleIndex % n) + n) % n;
  const i = Math.floor(wrapped);
  const t = wrapped - i;
  const a = track.cumulativeLength[i % n] ?? 0;
  const b = i + 1 < n ? track.cumulativeLength[i + 1] : track.cumulativeLength[n - 1] + segmentLength(track, n - 1, 0);
  return THREE.MathUtils.lerp(a, b, t);
}

function segmentLength(track: Track, aIndex: number, bIndex: number): number {
  const a = track.centerline[aIndex % track.centerline.length];
  const b = track.centerline[bIndex % track.centerline.length];
  return Math.hypot(b.x - a.x, b.z - a.z);
}

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * (
    2 * p1
    + (-p0 + p2) * t
    + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2
    + (-p0 + 3 * p1 - 3 * p2 + p3) * t3
  );
}

function catmullRomDerivative(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t;
  return 0.5 * (
    -p0 + p2
    + 2 * (2 * p0 - 5 * p1 + 4 * p2 - p3) * t
    + 3 * (-p0 + 3 * p1 - 3 * p2 + p3) * t2
  );
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/**
 * Vertical side walls of the track slab. For every centerline ring i, build
 * a quad strip from the banked surface top down to the corridor terrain
 * along innerPolygon and outerPolygon. Without these, the slab reads as a
 * floating ribbon — the side faces give it visible thickness.
 */
function buildSurfaceSides(track: Track, rendererMode: RendererMode): THREE.Mesh {
  const n = track.centerline.length;
  // 4 vertices per ring (innerTop, innerBot, outerTop, outerBot).
  const positions = new Float32Array(n * 4 * 3);
  const indices: number[] = [];

  for (let i = 0; i < n; i++) {
    const c = track.centerline[i];
    const inner = track.innerPolygon[i];
    const outer = track.outerPolygon[i];
    const innerTopY = trackSurfaceHeightAtSample(track, i, c.widthLeft) + TRACK_SURFACE_CLEARANCE;
    const outerTopY = trackSurfaceHeightAtSample(track, i, -c.widthRight) + TRACK_SURFACE_CLEARANCE;
    const innerBotY = innerTopY - SLAB_BOTTOM_DROP;
    const outerBotY = outerTopY - SLAB_BOTTOM_DROP;
    const base = i * 12;
    positions[base + 0] = inner.x;
    positions[base + 1] = innerTopY;
    positions[base + 2] = inner.z;
    positions[base + 3] = inner.x;
    positions[base + 4] = innerBotY;
    positions[base + 5] = inner.z;
    positions[base + 6] = outer.x;
    positions[base + 7] = outerTopY;
    positions[base + 8] = outer.z;
    positions[base + 9] = outer.x;
    positions[base + 10] = outerBotY;
    positions[base + 11] = outer.z;
  }

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const aInTop = i * 4 + 0;
    const aInBot = i * 4 + 1;
    const aOuTop = i * 4 + 2;
    const aOuBot = i * 4 + 3;
    const bInTop = j * 4 + 0;
    const bInBot = j * 4 + 1;
    const bOuTop = j * 4 + 2;
    const bOuBot = j * 4 + 3;
    // Inner wall faces toward the donut hole (away from corridor).
    indices.push(aInTop, aInBot, bInBot, aInTop, bInBot, bInTop);
    // Outer wall faces away from the donut (toward open desert).
    indices.push(aOuTop, bOuBot, aOuBot, aOuTop, bOuTop, bOuBot);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();

  const mat = new THREE.MeshStandardMaterial({
    color: 0x2c3138,
    roughness: 0.96,
    metalness: 0,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.receiveShadow = true;
  mesh.name = 'track-sides';
  tagValueBucket(mesh, 'mg');
  installAerialPerspective(mat, rendererMode);
  installValueComposition(mat, 'mg', rendererMode);
  return mesh;
}

function wearHash(x: number, y: number): number {
  let n = Math.floor(x * 374761393) + Math.floor(y * 668265263);
  n = (n ^ (n >>> 13)) * 1274126177;
  n = n ^ (n >>> 16);
  return ((n >>> 0) % 100000) / 100000;
}

function wearValueNoise(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = wearHash(xi, yi);
  const b = wearHash(xi + 1, yi);
  const c = wearHash(xi, yi + 1);
  const d = wearHash(xi + 1, yi + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

function wearFbm(x: number, y: number): number {
  let value = 0;
  let amp = 0.5;
  let freq = 1;
  let norm = 0;
  for (let o = 0; o < 3; o++) {
    value += amp * wearValueNoise(x * freq, y * freq);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return value / Math.max(norm, 1e-6);
}

// ────────────────────────────────────────────────────────────────────────────
// Sector decals (Phase C.2)
//
// Tiny canvas-rendered alpha planes laid at each checkpoint so the corridor
// stops feeling uniformly tiled. One decal per sector — tire mark, paint
// streak, repair patch. 256×128 canvas each, generated at runtime, no asset
// downloads. Sits at y=+0.005, transparent, depthWrite off so they don't
// fight the track surface for z-buffer.
// ────────────────────────────────────────────────────────────────────────────

const SECTOR_DECAL_KINDS = ['tireMark', 'paintStreak', 'repairPatch'] as const;
type SectorDecalKind = (typeof SECTOR_DECAL_KINDS)[number];

function buildSectorDecals(track: Track, rendererMode: RendererMode): THREE.Group {
  const group = new THREE.Group();
  group.name = 'sector-decals';
  if (typeof document === 'undefined') return group; // SSR / test guards
  const checkpoints = buildCheckpoints(track);
  for (const cp of checkpoints) {
    // Skip the start/finish sector — the start-bridge shadow plus the start
    // line already give that patch plenty of visual incident, and overlaying
    // a soft tire-mark canvas there washes out the procedural grain.
    if (cp.sectorIndex === 0) continue;
    const kind = SECTOR_DECAL_KINDS[cp.sectorIndex % SECTOR_DECAL_KINDS.length];
    const tex = makeSectorDecalTexture(kind);
    if (!tex) continue;
    const c = track.centerline[cp.centerlineIndex];
    const corridorWidth = c.widthLeft + c.widthRight;
    // Decal width = corridor width so the canvas content fits inside the
    // racing surface; the canvas alpha mask shapes the actual visible marks.
    const planeWidth = corridorWidth * 0.95;
    const planeLength = 4.5;
    const geom = new THREE.PlaneGeometry(planeWidth, planeLength);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      opacity: 0.4, // subtle scuff, not a billboard
    });
    const mesh = new THREE.Mesh(geom, mat);
    installAerialPerspective(mat, rendererMode);
    installValueComposition(mat, 'mg', rendererMode);
    tagValueBucket(mesh, 'mg');
    mesh.rotation.x = -Math.PI / 2;
    // Rotate to align decal length with track tangent.
    mesh.rotation.z = Math.atan2(cp.tx, cp.tz);
    mesh.position.set(cp.x, trackSurfaceHeightAtSample(track, cp.centerlineIndex, 0) + 0.024, cp.z);
    mesh.renderOrder = 2;
    mesh.name = `sector-decal-${cp.sectorIndex}-${kind}`;
    group.add(mesh);
  }
  return group;
}

function makeSectorDecalTexture(kind: SectorDecalKind): THREE.CanvasTexture | null {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, 256, 128);

  // All decals stay near pavement-tone — the goal is "this part of the track
  // is scuffed differently" rather than "billboard painted across the road".
  // Each canvas covers ~90% of the corridor; alpha mask shapes the wear.
  if (kind === 'tireMark') {
    // Two faint parallel rubber streaks running along the decal length.
    ctx.globalAlpha = 0.6;
    ctx.fillStyle = '#101216';
    for (const u of [0.36, 0.64]) {
      const cx = u * 256;
      for (let y = 4; y < 124; y += 3) {
        const jitter = Math.sin(y * 0.18) * 1.4 + (decalHash(y * 7, u * 100) - 0.5) * 2.0;
        const w = 4 + Math.sin(y * 0.07) * 1.2;
        ctx.fillRect(cx - w * 0.5 + jitter, y, w, 2);
      }
    }
  } else if (kind === 'paintStreak') {
    // Subtle drift scuff — soft darker patches with light brushing, no
    // bright paint. Reads as "tire smear" rather than a checkered banner.
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = '#1a1d22';
    for (let i = 0; i < 32; i++) {
      const x = decalHash(i, 3) * 256;
      const y = decalHash(i, 7) * 128;
      const r = 6 + decalHash(i, 11) * 10;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Long brushed streak in middle-third of decal.
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#0c0d10';
    for (let x = 12; x < 244; x += 6) {
      const yOff = (decalHash(x, 19) - 0.5) * 14;
      ctx.fillRect(x, 56 + yOff, 4 + decalHash(x, 7) * 4, 2);
    }
  } else if (kind === 'repairPatch') {
    // Asphalt repair seam — slightly darker rectangular patch with rough
    // edges. Subtle, like a re-paved square in the surface.
    ctx.globalAlpha = 0.32;
    ctx.fillStyle = '#15171b';
    const patchW = 132;
    const patchH = 56;
    const px = (256 - patchW) * 0.5;
    const py = (128 - patchH) * 0.5;
    ctx.beginPath();
    ctx.moveTo(px, py + (decalHash(0, 5) - 0.5) * 6);
    for (let x = 0; x <= patchW; x += 14) {
      ctx.lineTo(px + x, py + (decalHash(x, 5) - 0.5) * 6);
    }
    for (let y = 0; y <= patchH; y += 14) {
      ctx.lineTo(px + patchW + (decalHash(y, 9) - 0.5) * 6, py + y);
    }
    for (let x = patchW; x >= 0; x -= 14) {
      ctx.lineTo(px + x, py + patchH + (decalHash(x, 13) - 0.5) * 6);
    }
    for (let y = patchH; y >= 0; y -= 14) {
      ctx.lineTo(px + (decalHash(y, 17) - 0.5) * 6, py + y);
    }
    ctx.closePath();
    ctx.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  return texture;
}

function decalHash(a: number, b: number): number {
  let n = Math.floor(a) * 374761393 + Math.floor(b) * 668265263;
  n = (n ^ (n >>> 13)) * 1274126177;
  n = n ^ (n >>> 16);
  return ((n >>> 0) % 10000) / 10000;
}

// ────────────────────────────────────────────────────────────────────────────
// Scrape decals
//
// Pod racers don't have tires — when their chassis bottoms out at a corner
// apex it gouges the asphalt, exposing a lighter aggregate underlayer. The
// effect accumulates over time: dozens of racers, slightly different lines,
// produce a fan of parallel scrape streaks at every tight inside line.
//
// We place one decal plane per detected corner apex. The plane carries a
// procedurally drawn canvas of 4–7 parallel pale streaks with intermittent
// fade and a faint dark soot border, so the corner reads as well-used.
// ────────────────────────────────────────────────────────────────────────────

interface ScrapeApex {
  centerlineIndex: number;
  curvature: number;
  /** +1 = corner inside is left of centerline (widthLeft side), -1 = right side. */
  sign: 1 | -1;
}

function findCornerApexes(track: Track, style: StyleConfig): ScrapeApex[] {
  const segments = collectKerbSegments(track, style);
  const apexes: ScrapeApex[] = [];
  for (const seg of segments) {
    let peakIdx = seg.start;
    let peakCurv = 0;
    for (let i = seg.start; i < seg.end; i++) {
      const c = curvatureAt(track, i);
      if (c > peakCurv) {
        peakCurv = c;
        peakIdx = i;
      }
    }
    if (peakCurv < 0.18) continue;
    apexes.push({ centerlineIndex: peakIdx, curvature: peakCurv, sign: seg.sign });
  }
  return apexes;
}

function buildScrapeDecals(track: Track, style: StyleConfig, rendererMode: RendererMode): THREE.Group {
  const group = new THREE.Group();
  group.name = 'scrape-decals';
  if (typeof document === 'undefined') return group;
  const apexes = findCornerApexes(track, style);
  for (const apex of apexes) {
    const tex = makeScrapeTexture(apex);
    if (!tex) continue;
    const c = track.centerline[apex.centerlineIndex];
    const tang = track.tangents[apex.centerlineIndex];
    const planeLength = 6 + apex.curvature * 5;
    const planeWidth = 2.4 + apex.curvature * 1.4;
    // Place the decal at the inside-of-corner racing line — that's where the
    // chassis would scrape. racingLineCenterU is biased toward the inside;
    // we shift slightly further inside (toward the kerb) where gouges live.
    const racingLineU = racingLineCenterU(track, apex.centerlineIndex);
    const insideU = THREE.MathUtils.clamp(racingLineU + (apex.sign > 0 ? -0.07 : 0.07), 0.05, 0.95);
    const lateral = THREE.MathUtils.lerp(c.widthLeft, -c.widthRight, insideU);
    const nx = -tang.tz;
    const nz = tang.tx;
    const x = c.x + nx * lateral;
    const z = c.z + nz * lateral;
    const y = trackSurfaceHeightAtPosition(track, x, z) + TRACK_SURFACE_CLEARANCE + 0.014;
    const geom = new THREE.PlaneGeometry(planeWidth, planeLength);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      opacity: 0.85,
    });
    const mesh = new THREE.Mesh(geom, mat);
    installAerialPerspective(mat, rendererMode);
    installValueComposition(mat, 'mg', rendererMode);
    tagValueBucket(mesh, 'mg');
    mesh.rotation.x = -Math.PI / 2;
    mesh.rotation.z = Math.atan2(tang.tx, tang.tz);
    mesh.position.set(x, y, z);
    mesh.renderOrder = 3;
    mesh.name = `scrape-decal-${apex.centerlineIndex}`;
    group.add(mesh);
  }
  return group;
}

function makeScrapeTexture(apex: ScrapeApex): THREE.CanvasTexture | null {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, 256, 512);

  const seed = apex.centerlineIndex;
  const streakCount = 4 + Math.floor(apex.curvature * 5);
  const palette = ['#a59f93', '#8b8579', '#bcb4a4', '#7a7468'];
  // Soft dark soot wash framing the gouges — reads as smudged exhaust.
  ctx.globalAlpha = 0.18;
  ctx.fillStyle = '#0e0f12';
  for (let i = 0; i < 14; i++) {
    const cx = 60 + decalHash(seed, i * 7) * 136;
    const cy = 40 + decalHash(seed, i * 13 + 3) * 432;
    const r = 14 + decalHash(seed, i * 17 + 1) * 18;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  for (let s = 0; s < streakCount; s++) {
    const tx = streakCount === 1 ? 0.5 : s / (streakCount - 1);
    const baseX = 50 + tx * 156 + (decalHash(seed * 11, s) - 0.5) * 14;
    const drift = (decalHash(seed * 23, s) - 0.5) * 18;
    const startY = 30 + decalHash(seed, s * 5) * 110;
    const endY = 380 + decalHash(seed, s * 9) * 100;
    const color = palette[s % palette.length];

    ctx.fillStyle = color;
    for (let y = startY; y < endY; y += 2) {
      const t = (y - startY) / Math.max(1, endY - startY);
      const xJitter = drift * (t - 0.5)
        + Math.sin(y * 0.06 + s * 1.7) * 1.4
        + (decalHash(seed * 31 + s * 100, y) - 0.5) * 1.6;
      const w = 1.4 + Math.sin(y * 0.035 + s * 0.9) * 0.8;
      const fade = 1 - Math.abs(Math.sin(y * 0.014 + s * 2.3)) * 0.55;
      const tip = Math.min(1, t * 4) * Math.min(1, (1 - t) * 4);
      const alpha = fade * tip * (0.5 + decalHash(seed * 41, s) * 0.35);
      if (alpha > 0.05) {
        ctx.globalAlpha = alpha;
        ctx.fillRect(baseX + xJitter - w * 0.5, y, w, 2);
      }
    }

    // Subtle dark edge alongside each gouge — selling the "torn" look.
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#0a0b0e';
    for (let y = startY; y < endY; y += 5) {
      const t = (y - startY) / Math.max(1, endY - startY);
      const xJitter = drift * (t - 0.5) + Math.sin(y * 0.06 + s * 1.7) * 1.4;
      const tip = Math.min(1, t * 4) * Math.min(1, (1 - t) * 4);
      ctx.globalAlpha = 0.18 * tip;
      ctx.fillRect(baseX + xJitter - 3.5, y, 7, 1);
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 8;
  texture.needsUpdate = true;
  return texture;
}

function buildIntegratedEdgeLineGroup(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'track-edge-lines';
  group.userData.integratedIntoAsphaltShader = true;
  return group;
}

// Minimum segment span (centerline points) to be worth a kerb mesh.
const KERB_MIN_SEGMENT_POINTS = 8;

interface KerbSegment {
  start: number;
  end: number;
  sign: 1 | -1;
  curvature: number;
  style: KerbStyle;
}

interface InternalKerbSurface extends KerbSurface {
  segments: readonly KerbSegment[];
}

export function buildKerbSurface(track: Track): KerbSurface {
  return createKerbSurface(track, DEFAULT_STYLE_CONFIG);
}

function createKerbSurface(track: Track, style: StyleConfig): InternalKerbSurface {
  const segments = collectKerbSegments(track, style)
    .map((seg) => {
      const curvature = averageCurvature(track, seg.start, seg.end);
      return { ...seg, curvature, style: makeKerbStyle(style, curvature) };
    });

  const surface: InternalKerbSurface = {
    bandCount: segments.length,
    segments,
    isOnKerb: (x: number, z: number) => isPointOnKerb(track, surface.segments, x, z),
    heightAt: (x: number, z: number) => kerbHeightAt(track, surface.segments, x, z),
  };
  return surface;
}

function collectKerbSegments(track: Track, style: StyleConfig): Array<{ start: number; end: number; sign: 1 | -1 }> {
  if (!style.kerbs.enabled) return [];
  if (style.kerbs.continuous) {
    return [
      { start: 0, end: track.centerline.length, sign: 1 },
      { start: 0, end: track.centerline.length, sign: -1 },
    ];
  }
  const n = track.centerline.length;
  // Detect sustained heading change over a longer centerline window. Local
  // point-to-point tangent deltas are too noisy: they create tiny kerbs on
  // straights and miss broad, visually obvious turns.
  let segmentStart = -1;
  let segmentSign: 1 | -1 | 0 = 0;
  const segments: Array<{ start: number; end: number; sign: 1 | -1 }> = [];

  const closeSegment = (end: number) => {
    if (segmentStart === -1) return;
    const trimmedStart = segmentStart + KERB_SEGMENT_TRIM_POINTS;
    const trimmedEnd = end - KERB_SEGMENT_TRIM_POINTS;
    if (
      trimmedEnd - trimmedStart >= KERB_MIN_SEGMENT_POINTS
      && segmentSign !== 0
      && segmentArc(track, trimmedStart, trimmedEnd) >= KERB_MIN_SEGMENT_ARC
    ) {
      segments.push({
        start: trimmedStart,
        end: trimmedEnd,
        sign: insideKerbSign(track, trimmedStart, trimmedEnd),
      });
    }
    segmentStart = -1;
    segmentSign = 0;
  };

  for (let i = 0; i < n; i++) {
    const delta = windowHeadingChange(track, i);
    if (Math.abs(delta) < KERB_MIN_WINDOW_HEADING_CHANGE) {
      closeSegment(i);
      continue;
    }
    const sign = delta > 0 ? -1 : 1;
    if (segmentStart === -1) {
      segmentStart = i;
      segmentSign = sign;
    } else if (sign !== segmentSign) {
      closeSegment(i);
      segmentStart = i;
      segmentSign = sign;
    }
  }
  closeSegment(n);

  return mergeNearbyKerbSegments(track, segments);
}

function mergeNearbyKerbSegments(
  track: Track,
  segments: Array<{ start: number; end: number; sign: 1 | -1 }>,
): Array<{ start: number; end: number; sign: 1 | -1 }> {
  const merged: Array<{ start: number; end: number; sign: 1 | -1 }> = [];
  for (const segment of segments) {
    const prev = merged[merged.length - 1];
    if (prev && prev.sign === segment.sign && segment.start - prev.end <= KERB_MERGE_GAP_POINTS) {
      prev.end = segment.end;
    } else {
      merged.push({ ...segment });
    }
  }
  return merged.filter((seg) => segmentArc(track, seg.start, seg.end) >= KERB_MIN_SEGMENT_ARC);
}

function buildKerbs(track: Track, segments: readonly KerbSegment[], rendererMode: RendererMode = 'webgl2'): THREE.Group {
  const group = new THREE.Group();
  group.name = 'track-kerbs';
  for (const seg of segments) {
    addKerbMesh(group, track, seg, rendererMode);
  }

  return group;
}

function rebuildKerbs(
  group: THREE.Group,
  surface: InternalKerbSurface,
  track: Track,
  style: StyleConfig,
  rendererMode: RendererMode = 'webgl2',
): void {
  disposeObjectChildren(group);
  const segments = collectKerbSegments(track, style).map((seg) => {
    const curvature = averageCurvature(track, seg.start, seg.end);
    return { ...seg, curvature, style: makeKerbStyle(style, curvature) };
  });
  surface.segments = segments;
  for (const seg of segments) addKerbMesh(group, track, seg, rendererMode);
}

interface KerbStyle {
  /** Peak strip width in meters (max width at the apex of the segment). */
  peakWidth: number;
  /** Extra width added at local curvature peaks for continuous kerbs. */
  cornerWidthBoost: number;
  /** Stripe length in meters along the corner arc. */
  stripeLength: number;
  /** Apex stripe color, alternating with white. */
  accent: { r: number; g: number; b: number };
  white: { r: number; g: number; b: number };
  /** Top of the kerb above the track surface, in meters. */
  topHeight: number;
  /** Width retained at segment ends (0 = full taper, 1 = no taper). */
  endTaperFloor: number;
  ridgeStrength: number;
  rubberInfluence: number;
  rubberColor: { r: number; g: number; b: number };
  rubberLongScale: number;
  roughness: number;
  metalness: number;
  envMapIntensity: number;
  emissive: { r: number; g: number; b: number };
  emissiveIntensity: number;
}

interface KerbShaderUniforms {
  uKerbStripeLength: { value: number };
  uKerbRed: { value: THREE.Color };
  uKerbWhite: { value: THREE.Color };
  uKerbRidgeStrength: { value: number };
  uKerbRubberInfluence: { value: number };
  uKerbRubberColor: { value: THREE.Color };
  uKerbRubberLongScale: { value: number };
}

const KERB_WHITE = { r: 1.00, g: 1.00, b: 1.00 } as const;
const KERB_MAX_QUAD_LENGTH = 0.9;
const KERB_TRACK_EDGE_OVERLAP = 0.45;
// Lateral distance over which the inner ramp climbs from track surface to
// kerb top. The remaining (KERB_TRACK_EDGE_OVERLAP - KERB_RAMP_RISE_LATERAL)
// metres become the foot of the ramp that overlaps onto the racing surface.
const KERB_RAMP_RISE_LATERAL = 0.30;
const KERB_HIT_TEST_PAD = 1.4;
const KERB_STYLES: ReadonlyArray<{ minCurvature: number; style: KerbStyle }> = [
  // True hairpins — wider/taller red rumble strip, short stripes.
  {
    minCurvature: 0.85,
    style: {
      peakWidth: 5.2,
      cornerWidthBoost: 0,
      stripeLength: 1.45,
      accent: { r: 0.96, g: 0.07, b: 0.08 },
      white: KERB_WHITE,
      topHeight: 0.14,
      endTaperFloor: 0.20,
      ridgeStrength: 0.6,
      rubberInfluence: 0.4,
      rubberColor: { r: 0, g: 0, b: 0 },
      rubberLongScale: 0.37,
      roughness: 0.72,
      metalness: 0,
      envMapIntensity: 0.38,
      emissive: { r: 0, g: 0, b: 0 },
      emissiveIntensity: 0,
    },
  },
  // Medium corners — vivid F1 red, classic stripe spacing.
  {
    minCurvature: 0.30,
    style: {
      peakWidth: 4.2,
      cornerWidthBoost: 0,
      stripeLength: 1.8,
      accent: { r: 0.94, g: 0.11, b: 0.13 },
      white: KERB_WHITE,
      topHeight: 0.10,
      endTaperFloor: 0.30,
      ridgeStrength: 0.6,
      rubberInfluence: 0.4,
      rubberColor: { r: 0, g: 0, b: 0 },
      rubberLongScale: 0.37,
      roughness: 0.72,
      metalness: 0,
      envMapIntensity: 0.38,
      emissive: { r: 0, g: 0, b: 0 },
      emissiveIntensity: 0,
    },
  },
  // Fast sweepers — same red, narrower strip and longer stripes for a
  // painted-rumble look (still red so the lap reads coherent, instead of
  // the previous near-invisible dark-red tier).
  {
    minCurvature: 0,
    style: {
      peakWidth: 2.6,
      cornerWidthBoost: 0,
      stripeLength: 2.2,
      accent: { r: 0.86, g: 0.12, b: 0.14 },
      white: KERB_WHITE,
      topHeight: 0.06,
      endTaperFloor: 0.45,
      ridgeStrength: 0.6,
      rubberInfluence: 0.4,
      rubberColor: { r: 0, g: 0, b: 0 },
      rubberLongScale: 0.37,
      roughness: 0.72,
      metalness: 0,
      envMapIntensity: 0.38,
      emissive: { r: 0, g: 0, b: 0 },
      emissiveIntensity: 0,
    },
  },
];

function pickKerbStyle(curvature: number): KerbStyle {
  for (const { minCurvature, style } of KERB_STYLES) {
    if (curvature >= minCurvature) return style;
  }
  return KERB_STYLES[KERB_STYLES.length - 1].style;
}

function makeKerbStyle(config: StyleConfig, curvature: number): KerbStyle {
  if (!config.kerbs.continuous) {
    const legacy = pickKerbStyle(curvature);
    return {
      ...legacy,
      peakWidth: config.kerbs.baseWidth + legacy.peakWidth * 0.35 + curvature * config.kerbs.cornerWidthBoost,
      cornerWidthBoost: 0,
      stripeLength: config.kerbs.stripeLength,
      accent: colorToRgb(config.kerbs.redHex),
      white: colorToRgb(config.kerbs.whiteHex),
      topHeight: config.kerbs.height,
      endTaperFloor: legacy.endTaperFloor,
      ridgeStrength: config.kerbs.ridgeStrength,
      rubberInfluence: config.kerbs.rubberInfluence,
      rubberColor: colorToRgb(config.asphalt.rubberStreakHex),
      rubberLongScale: config.asphalt.rubberStreakLongScale,
      roughness: config.kerbs.roughness,
      metalness: config.kerbs.metalness,
      envMapIntensity: config.kerbs.envMapIntensity,
      emissive: colorToRgb(config.kerbs.emissiveHex),
      emissiveIntensity: config.kerbs.emissiveIntensity,
    };
  }
  return {
    peakWidth: config.kerbs.baseWidth,
    cornerWidthBoost: config.kerbs.cornerWidthBoost,
    stripeLength: config.kerbs.stripeLength,
    accent: colorToRgb(config.kerbs.redHex),
    white: colorToRgb(config.kerbs.whiteHex),
    topHeight: config.kerbs.height,
    endTaperFloor: 1,
    ridgeStrength: config.kerbs.ridgeStrength,
    rubberInfluence: config.kerbs.rubberInfluence,
    rubberColor: colorToRgb(config.asphalt.rubberStreakHex),
    rubberLongScale: config.asphalt.rubberStreakLongScale,
    roughness: config.kerbs.roughness,
    metalness: config.kerbs.metalness,
    envMapIntensity: config.kerbs.envMapIntensity,
    emissive: colorToRgb(config.kerbs.emissiveHex),
    emissiveIntensity: config.kerbs.emissiveIntensity,
  };
}

function colorToRgb(hex: string): { r: number; g: number; b: number } {
  const color = new THREE.Color(hex);
  return { r: color.r, g: color.g, b: color.b };
}

/**
 * Vertex roles for the kerb cross-section. Each station emits four vertices,
 * laid out from the racing-surface side (inboard) outward. Ordering is
 * load-bearing: index = role.
 *
 *   1 top_in ─────── 2 top_out
 *      /│              │
 *     / │              │
 * 0 bot_in            3 outer_bot
 *  ── track ──────┘  └── track ──
 */
type KerbVertexRole = 0 | 1 | 2 | 3;
const KERB_VERT_BOT_IN: KerbVertexRole = 0;
const KERB_VERT_TOP_IN: KerbVertexRole = 1;
const KERB_VERT_TOP_OUT: KerbVertexRole = 2;
const KERB_VERT_OUTER_BOT: KerbVertexRole = 3;
const KERB_VERTS_PER_STATION = 4;

function addKerbMesh(
  group: THREE.Group,
  track: Track,
  seg: KerbSegment,
  rendererMode: RendererMode = 'webgl2',
): void {
  const { start, end, sign, style } = seg;
  const count = end - start;
  if (count < 2) return;

  const samples: KerbSample[] = [];
  let segmentArc = 0;
  const steps = Math.max(1, (count - 1) * KERB_LONGITUDINAL_SUBDIVISIONS);
  let prev: RenderRibbonSample | null = null;
  for (let k = 0; k <= steps; k++) {
    const tNorm = steps > 0 ? k / steps : 0.5;
    const sampleIndex = THREE.MathUtils.lerp(start, end - 1, tNorm);
    const renderSample = sampleRenderRibbon(track, sampleIndex);
    if (prev) segmentArc += Math.hypot(renderSample.x - prev.x, renderSample.z - prev.z);
    prev = renderSample;
    samples.push({
      arc: segmentArc,
      x: renderSample.x,
      z: renderSample.z,
      tx: renderSample.tx,
      tz: renderSample.tz,
      widthLeft: renderSample.widthLeft,
      widthRight: renderSample.widthRight,
      curvature: curvatureAt(track, Math.round(sampleIndex)),
      worldArc: renderSample.arc,
      tNorm,
    });
  }

  const stations = kerbStations(samples, style.stripeLength);
  const quadCount = Math.max(0, stations.length - 1);
  // 8 vertices per quad: 4 cross-section verts at station A + 4 at station B.
  const vertsPerQuad = KERB_VERTS_PER_STATION * 2;
  const pos = new Float32Array(quadCount * vertsPerQuad * 3);
  const arcs = new Float32Array(quadCount * vertsPerQuad);
  const roles = new Float32Array(quadCount * vertsPerQuad);
  const curvatures = new Float32Array(quadCount * vertsPerQuad);
  const uvs = new Float32Array(quadCount * vertsPerQuad * 2);
  const idx: number[] = [];

  for (let q = 0; q < quadCount; q++) {
    const a = interpolateKerbSample(samples, stations[q]);
    const b = interpolateKerbSample(samples, stations[q + 1]);
    const base = q * vertsPerQuad;
    // Station A: roles 0..3
    writeKerbVertex(track, pos, (base + KERB_VERT_BOT_IN) * 3, a, sign, style, KERB_VERT_BOT_IN);
    writeKerbVertex(track, pos, (base + KERB_VERT_TOP_IN) * 3, a, sign, style, KERB_VERT_TOP_IN);
    writeKerbVertex(track, pos, (base + KERB_VERT_TOP_OUT) * 3, a, sign, style, KERB_VERT_TOP_OUT);
    writeKerbVertex(track, pos, (base + KERB_VERT_OUTER_BOT) * 3, a, sign, style, KERB_VERT_OUTER_BOT);
    // Station B: roles 4..7 (offset by KERB_VERTS_PER_STATION)
    const bBase = base + KERB_VERTS_PER_STATION;
    writeKerbVertex(track, pos, (bBase + KERB_VERT_BOT_IN) * 3, b, sign, style, KERB_VERT_BOT_IN);
    writeKerbVertex(track, pos, (bBase + KERB_VERT_TOP_IN) * 3, b, sign, style, KERB_VERT_TOP_IN);
    writeKerbVertex(track, pos, (bBase + KERB_VERT_TOP_OUT) * 3, b, sign, style, KERB_VERT_TOP_OUT);
    writeKerbVertex(track, pos, (bBase + KERB_VERT_OUTER_BOT) * 3, b, sign, style, KERB_VERT_OUTER_BOT);

    for (let role = 0; role < KERB_VERTS_PER_STATION; role++) {
      const aVertex = base + role;
      arcs[aVertex] = a.arc;
      roles[aVertex] = role;
      curvatures[aVertex] = a.curvature;
      uvs[aVertex * 2 + 0] = role / Math.max(1, KERB_VERTS_PER_STATION - 1);
      uvs[aVertex * 2 + 1] = a.arc / Math.max(0.001, style.stripeLength * 2);

      const bVertex = bBase + role;
      arcs[bVertex] = b.arc;
      roles[bVertex] = role;
      curvatures[bVertex] = b.curvature;
      uvs[bVertex * 2 + 0] = role / Math.max(1, KERB_VERTS_PER_STATION - 1);
      uvs[bVertex * 2 + 1] = b.arc / Math.max(0.001, style.stripeLength * 2);
    }

    // Three face strips per quad. Vertex winding is chosen so triangle normals
    // face outward; DoubleSide rendering keeps both orientations visible.
    const aBot = base + KERB_VERT_BOT_IN;
    const aTopIn = base + KERB_VERT_TOP_IN;
    const aTopOut = base + KERB_VERT_TOP_OUT;
    const aOuter = base + KERB_VERT_OUTER_BOT;
    const bBot = bBase + KERB_VERT_BOT_IN;
    const bTopIn = bBase + KERB_VERT_TOP_IN;
    const bTopOut = bBase + KERB_VERT_TOP_OUT;
    const bOuter = bBase + KERB_VERT_OUTER_BOT;
    // Inner ramp (track-side face the racer climbs)
    idx.push(aBot, aTopIn, bTopIn, aBot, bTopIn, bBot);
    // Top face (the surface the racer rides on)
    idx.push(aTopIn, aTopOut, bTopOut, aTopIn, bTopOut, bTopIn);
    // Outer drop (back face away from the racing surface)
    idx.push(aTopOut, aOuter, bOuter, aTopOut, bOuter, bTopOut);

    // End caps: close the prism's start (q == 0) and end (q == quadCount - 1)
    // faces so the kerb looks solid head-on. Without these, the front of each
    // segment shows an open hole through the geometry.
    if (q === 0) {
      idx.push(aBot, aTopOut, aTopIn, aBot, aOuter, aTopOut);
    }
    if (q === quadCount - 1) {
      idx.push(bBot, bTopIn, bTopOut, bBot, bTopOut, bOuter);
    }
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geom.setAttribute('aKerbArc', new THREE.BufferAttribute(arcs, 1));
  geom.setAttribute('aKerbRole', new THREE.BufferAttribute(roles, 1));
  geom.setAttribute('aKerbCurvature', new THREE.BufferAttribute(curvatures, 1));
  geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geom.setIndex(idx);
  geom.computeVertexNormals();
  const mat = makeKerbMaterial(style, rendererMode);
  const mesh = new THREE.Mesh(geom, mat);
  mesh.receiveShadow = true;
  mesh.castShadow = false;
  mesh.userData.kerbSegment = { start, end, sign };
  tagValueBucket(mesh, 'mg');
  group.add(mesh);
}

function makeKerbMaterial(style: KerbStyle, rendererMode: RendererMode = 'webgl2'): THREE.MeshStandardMaterial {
  if (rendererMode === 'webgpu') {
    return makeWebGPUKerbNodeMaterial(style) as unknown as THREE.MeshStandardMaterial;
  }
  const mat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    map: null,
    side: THREE.DoubleSide,
    roughness: style.roughness,
    metalness: style.metalness,
    envMapIntensity: style.envMapIntensity,
    emissive: new THREE.Color(style.emissive.r, style.emissive.g, style.emissive.b),
    emissiveIntensity: style.emissiveIntensity,
    toneMapped: true,
  });
  installKerbShader(mat, style);
  return mat;
}

const kerbColorNode = makeWgslFn(`
fn kerb_color(
  arc: f32,
  role: f32,
  curvature: f32,
  stripeLength: f32,
  accent: vec3<f32>,
  white: vec3<f32>,
  rubberColor: vec3<f32>,
  rubberInfluence: f32,
  rubberLongScale: f32,
  ridgeStrength: f32
) -> vec3<f32> {
  let stripeIndex = floor(arc / max(0.001, stripeLength));
  let whiteMask = step(0.5, fract(stripeIndex * 0.5));
  let topMask = 1.0 - smoothstep(0.35, 0.75, abs(role - 1.5));
  let outerWear = (1.0 - smoothstep(0.0, 0.55, abs(role - 2.0))) * topMask;
  let stripeDust = asphalt_fbm(vec2<f32>(arc * 0.52, role * 3.4 + 9.1));
  var stripeColor = mix(accent, white, whiteMask);
  let luma = dot(stripeColor, vec3<f32>(0.299, 0.587, 0.114));
  let dusty = mix(stripeColor, vec3<f32>(luma * 0.78, luma * 0.78, luma * 0.78), outerWear * 0.36 + stripeDust * 0.08);
  let curvatureRubber = clamp(0.25 + curvature * 2.4, 0.0, 1.0);
  let rubberNoise = asphalt_fbm(vec2<f32>(arc * max(0.01, rubberLongScale) + 13.7, role * 5.0 + 31.2));
  let rubber = smoothstep(0.42, 0.92, rubberNoise) * topMask * curvatureRubber * rubberInfluence;
  let ridge = 0.94 + sin(role * 3.14159265) * 0.08 * ridgeStrength * topMask;
  return mix(dusty * ridge, rubberColor, clamp(rubber, 0.0, 0.82));
}
`, [asphaltFbmNode]);

function makeWebGPUKerbNodeMaterial(style: KerbStyle): MeshStandardNodeMaterial {
  const material = new MeshStandardNodeMaterial({
    color: 0xffffff,
    side: THREE.DoubleSide,
    roughness: style.roughness,
    metalness: style.metalness,
    envMapIntensity: style.envMapIntensity,
    emissive: new THREE.Color(style.emissive.r, style.emissive.g, style.emissive.b),
    emissiveIntensity: style.emissiveIntensity,
    toneMapped: true,
  });
  const arc = makeNodeAttribute('aKerbArc', 'float');
  const role = makeNodeAttribute('aKerbRole', 'float');
  const curvature = makeNodeAttribute('aKerbCurvature', 'float');
  material.colorNode = kerbColorNode({
    arc,
    role,
    curvature,
    stripeLength: makeNodeUniform(style.stripeLength),
    accent: makeNodeUniform(new THREE.Vector3(style.accent.r, style.accent.g, style.accent.b)),
    white: makeNodeUniform(new THREE.Vector3(style.white.r, style.white.g, style.white.b)),
    rubberColor: makeNodeUniform(new THREE.Vector3(style.rubberColor.r, style.rubberColor.g, style.rubberColor.b)),
    rubberInfluence: makeNodeUniform(style.rubberInfluence),
    rubberLongScale: makeNodeUniform(style.rubberLongScale),
    ridgeStrength: makeNodeUniform(style.ridgeStrength),
  });
  material.fog = true;
  material.userData.webgpuKerbMaterial = true;
  return material;
}

function installKerbShader(material: THREE.MeshStandardMaterial, style: KerbStyle): void {
  const uniforms: KerbShaderUniforms = {
    uKerbStripeLength: { value: style.stripeLength },
    uKerbRed: { value: new THREE.Color(style.accent.r, style.accent.g, style.accent.b) },
    uKerbWhite: { value: new THREE.Color(style.white.r, style.white.g, style.white.b) },
    uKerbRidgeStrength: { value: style.ridgeStrength },
    uKerbRubberInfluence: { value: style.rubberInfluence },
    uKerbRubberColor: { value: new THREE.Color(style.rubberColor.r, style.rubberColor.g, style.rubberColor.b) },
    uKerbRubberLongScale: { value: style.rubberLongScale },
  };
  material.userData.kerbShaderUniforms = uniforms;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace(
        'void main() {',
        `
attribute float aKerbArc;
attribute float aKerbRole;
attribute float aKerbCurvature;
varying float vKerbArc;
varying float vKerbRole;
varying float vKerbCurvature;
varying vec3 vKerbWorldPosition;
varying vec3 vKerbWorldNormal;

void main() {
        `.trim(),
      )
      .replace(
        '#include <defaultnormal_vertex>',
        `
#include <defaultnormal_vertex>
vKerbWorldNormal = normalize(transformedNormal);
        `.trim(),
      )
      .replace(
        '#include <begin_vertex>',
        `
#include <begin_vertex>
vKerbArc = aKerbArc;
vKerbRole = aKerbRole;
vKerbCurvature = aKerbCurvature;
        `.trim(),
      )
      .replace(
        '#include <worldpos_vertex>',
        `
#include <worldpos_vertex>
vKerbWorldPosition = worldPosition.xyz;
        `.trim(),
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        `
uniform float uKerbStripeLength;
uniform vec3 uKerbRed;
uniform vec3 uKerbWhite;
uniform float uKerbRidgeStrength;
uniform float uKerbRubberInfluence;
uniform vec3 uKerbRubberColor;
uniform float uKerbRubberLongScale;
varying float vKerbArc;
varying float vKerbRole;
varying float vKerbCurvature;
varying vec3 vKerbWorldPosition;
varying vec3 vKerbWorldNormal;

float kerbHash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float kerbNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = kerbHash(i);
  float b = kerbHash(i + vec2(1.0, 0.0));
  float c = kerbHash(i + vec2(0.0, 1.0));
  float d = kerbHash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float kerbFbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * kerbNoise(p);
    p *= 2.03;
    a *= 0.5;
  }
  return v;
}

float kerbTopMask() {
  return 1.0 - smoothstep(0.35, 0.75, abs(vKerbRole - 1.5));
}

void main() {
        `.trim(),
      )
      .replace(
        '#include <color_fragment>',
        `
float stripe = mod(floor(vKerbArc / max(0.001, uKerbStripeLength)), 2.0);
vec3 kerbColor = mix(uKerbRed, uKerbWhite, step(0.5, stripe));
float topMask = kerbTopMask();
float outerWear = (1.0 - smoothstep(0.0, 0.55, abs(vKerbRole - 2.0))) * topMask;
float stripeDust = kerbFbm(vec2(vKerbArc * 0.52, vKerbRole * 3.4 + 9.1));
vec3 dusty = mix(kerbColor, vec3(dot(kerbColor, vec3(0.299, 0.587, 0.114))) * 0.78, outerWear * 0.36 + stripeDust * 0.08);
float curvatureRubber = clamp(0.25 + vKerbCurvature * 2.4, 0.0, 1.0);
float rubberNoise = kerbFbm(vec2(vKerbArc * max(0.01, uKerbRubberLongScale) + 13.7, vKerbRole * 5.0 + 31.2));
float rubber = smoothstep(0.42, 0.92, rubberNoise) * topMask * curvatureRubber * uKerbRubberInfluence;
diffuseColor.rgb = mix(dusty, uKerbRubberColor, rubber);
        `.trim(),
      )
      .replace(
        '#include <normal_fragment_maps>',
        `
#include <normal_fragment_maps>
float ridgeTopMask = kerbTopMask();
float ridgePhase = vKerbArc * 24.0;
float ridgeSlope = cos(ridgePhase) * uKerbRidgeStrength * ridgeTopMask * 0.18;
vec3 kerbAlongRaw =
  dFdx(vKerbWorldPosition) * dFdx(vKerbArc)
  + dFdy(vKerbWorldPosition) * dFdy(vKerbArc);
vec3 kerbAlong = length(kerbAlongRaw) > 0.0001 ? normalize(kerbAlongRaw) : vec3(0.0, 0.0, 0.0);
vec3 kerbWorldNormal = normalize(vKerbWorldNormal - kerbAlong * ridgeSlope);
normal = normalize((viewMatrix * vec4(kerbWorldNormal, 0.0)).xyz);
        `.trim(),
      );
  };
}

function isPointOnKerb(
  track: Track,
  segments: readonly KerbSegment[],
  x: number,
  z: number,
): boolean {
  if (segments.length === 0) return false;

  const n = track.centerline.length;
  for (const seg of segments) {
    const count = seg.end - seg.start;
    const denom = Math.max(1, count - 1);
    let best:
      | {
        i: number;
        t: number;
        closestX: number;
        closestZ: number;
        distSq: number;
      }
      | null = null;

    for (let i = seg.start; i < seg.end - 1; i++) {
      const a = track.centerline[i % n];
      const b = track.centerline[(i + 1) % n];
      const sx = b.x - a.x;
      const sz = b.z - a.z;
      const lenSq = sx * sx + sz * sz || 1e-9;
      const rawT = ((x - a.x) * sx + (z - a.z) * sz) / lenSq;
      if (rawT < -0.55 || rawT > 1.55) continue;
      const t = THREE.MathUtils.clamp(rawT, 0, 1);
      const closestX = a.x + sx * t;
      const closestZ = a.z + sz * t;
      const dx = x - closestX;
      const dz = z - closestZ;
      const distSq = dx * dx + dz * dz;
      if (!best || distSq < best.distSq) {
        best = { i, t, closestX, closestZ, distSq };
      }
    }

    if (!best) continue;
    const a = track.centerline[best.i % n];
    const b = track.centerline[(best.i + 1) % n];
    const ta = track.tangents[best.i % n];
    const tb = track.tangents[(best.i + 1) % n];
    const rawTx = THREE.MathUtils.lerp(ta.tx, tb.tx, best.t);
    const rawTz = THREE.MathUtils.lerp(ta.tz, tb.tz, best.t);
    const tangentLen = Math.hypot(rawTx, rawTz) || 1;
    const tx = rawTx / tangentLen;
    const tz = rawTz / tangentLen;
    const nx = -tz;
    const nz = tx;
    const signedLateral = (x - best.closestX) * nx + (z - best.closestZ) * nz;
    const widthLeft = THREE.MathUtils.lerp(a.widthLeft, b.widthLeft, best.t);
    const widthRight = THREE.MathUtils.lerp(a.widthRight, b.widthRight, best.t);
    const tNorm = THREE.MathUtils.clamp((best.i - seg.start + best.t) / denom, 0, 1);
    const taper = Math.sin(Math.PI * tNorm);
    const localCurvature = THREE.MathUtils.lerp(curvatureAt(track, best.i), curvatureAt(track, best.i + 1), best.t);
    const stripWidth = (seg.style.peakWidth + localCurvature * seg.style.cornerWidthBoost)
      * Math.max(seg.style.endTaperFloor, taper);
    const edgeOffset = seg.sign > 0 ? widthLeft : -widthRight;
    const inboard = edgeOffset - KERB_TRACK_EDGE_OVERLAP * seg.sign;
    const outboard = edgeOffset + stripWidth * seg.sign;
    const minLateral = Math.min(inboard, outboard) - KERB_HIT_TEST_PAD;
    const maxLateral = Math.max(inboard, outboard) + KERB_HIT_TEST_PAD;

    if (signedLateral >= minLateral && signedLateral <= maxLateral) {
      return true;
    }
  }
  return false;
}

/**
 * Top-of-kerb height at world (x, z) relative to the track surface, matching
 * the prism geometry built by `addKerbMesh`. Returns 0 when off any kerb,
 * ramps linearly from the racing surface up to `topHeight * endTaperScale`
 * across the inner chamfer, and tapers to 0 at segment ends in lockstep with
 * the visual taper. When kerbs overlap, the racer sits on the tallest one.
 */
function kerbHeightAt(
  track: Track,
  segments: readonly KerbSegment[],
  x: number,
  z: number,
): number {
  if (segments.length === 0) return 0;
  const n = track.centerline.length;
  const rampTopInboard = Math.max(0, KERB_TRACK_EDGE_OVERLAP - KERB_RAMP_RISE_LATERAL);
  let maxHeight = 0;

  for (const seg of segments) {
    const count = seg.end - seg.start;
    const denom = Math.max(1, count - 1);
    let best:
      | { i: number; t: number; closestX: number; closestZ: number; distSq: number }
      | null = null;

    for (let i = seg.start; i < seg.end - 1; i++) {
      const a = track.centerline[i % n];
      const b = track.centerline[(i + 1) % n];
      const sx = b.x - a.x;
      const sz = b.z - a.z;
      const lenSq = sx * sx + sz * sz || 1e-9;
      const rawT = ((x - a.x) * sx + (z - a.z) * sz) / lenSq;
      if (rawT < -0.05 || rawT > 1.05) continue;
      const t = THREE.MathUtils.clamp(rawT, 0, 1);
      const closestX = a.x + sx * t;
      const closestZ = a.z + sz * t;
      const dx = x - closestX;
      const dz = z - closestZ;
      const distSq = dx * dx + dz * dz;
      if (!best || distSq < best.distSq) {
        best = { i, t, closestX, closestZ, distSq };
      }
    }

    if (!best) continue;
    const a = track.centerline[best.i % n];
    const b = track.centerline[(best.i + 1) % n];
    const ta = track.tangents[best.i % n];
    const tb = track.tangents[(best.i + 1) % n];
    const rawTx = THREE.MathUtils.lerp(ta.tx, tb.tx, best.t);
    const rawTz = THREE.MathUtils.lerp(ta.tz, tb.tz, best.t);
    const tangentLen = Math.hypot(rawTx, rawTz) || 1;
    const tx = rawTx / tangentLen;
    const tz = rawTz / tangentLen;
    const nx = -tz;
    const nz = tx;
    const signedLateral = (x - best.closestX) * nx + (z - best.closestZ) * nz;
    const widthLeft = THREE.MathUtils.lerp(a.widthLeft, b.widthLeft, best.t);
    const widthRight = THREE.MathUtils.lerp(a.widthRight, b.widthRight, best.t);
    const tNorm = THREE.MathUtils.clamp((best.i - seg.start + best.t) / denom, 0, 1);
    const taper = Math.sin(Math.PI * tNorm);
    const taperScale = Math.max(seg.style.endTaperFloor, taper);
    const localCurvature = THREE.MathUtils.lerp(
      curvatureAt(track, best.i),
      curvatureAt(track, best.i + 1),
      best.t,
    );
    const stripWidth = (seg.style.peakWidth + localCurvature * seg.style.cornerWidthBoost)
      * taperScale;
    const edgeOffset = seg.sign > 0 ? widthLeft : -widthRight;
    // Outboard distance from the racing-surface edge, in metres. Negative
    // values are inboard (on the track), positive values are toward the
    // outside of the corner.
    const outboardDist = (signedLateral - edgeOffset) * seg.sign;
    // Height stays at full topHeight along the segment (the visible mesh
    // doesn't taper vertically — only the width pinches at the ends), so the
    // racer rides at a constant height while on the kerb's flat top.
    const peak = seg.style.topHeight;

    let height = 0;
    if (outboardDist <= -KERB_TRACK_EDGE_OVERLAP) {
      height = 0;
    } else if (outboardDist >= -rampTopInboard && outboardDist <= stripWidth) {
      height = peak;
    } else if (outboardDist < -rampTopInboard) {
      // Inner ramp: linear rise from 0 at the foot to peak at the ramp top.
      const t = (outboardDist + KERB_TRACK_EDGE_OVERLAP) / KERB_RAMP_RISE_LATERAL;
      height = peak * THREE.MathUtils.clamp(t, 0, 1);
    } else {
      // Past the outer edge — no lift; the outer face is a vertical drop.
      height = 0;
    }

    if (height > maxHeight) maxHeight = height;
  }
  return maxHeight;
}

interface KerbSample {
  arc: number;
  x: number;
  z: number;
  tx: number;
  tz: number;
  widthLeft: number;
  widthRight: number;
  curvature: number;
  worldArc: number;
  tNorm: number;
}

function kerbStations(samples: KerbSample[], stripeLength: number): number[] {
  const stations: number[] = [0];
  for (let i = 0; i < samples.length - 1; i++) {
    const start = samples[i].arc;
    const end = samples[i + 1].arc;
    const anchors = [start];
    let boundary = (Math.floor(start / stripeLength) + 1) * stripeLength;
    while (boundary < end - 1e-6) {
      anchors.push(boundary);
      boundary += stripeLength;
    }
    anchors.push(end);

    for (let a = 0; a < anchors.length - 1; a++) {
      const from = anchors[a];
      const to = anchors[a + 1];
      const steps = Math.max(1, Math.ceil((to - from) / KERB_MAX_QUAD_LENGTH));
      for (let s = 1; s <= steps; s++) {
        const station = from + ((to - from) * s) / steps;
        if (station > stations[stations.length - 1] + 1e-6) stations.push(station);
      }
    }
  }
  return stations;
}

function interpolateKerbSample(samples: KerbSample[], station: number): KerbSample {
  let i = 0;
  while (i < samples.length - 2 && samples[i + 1].arc < station) i++;
  const a = samples[i];
  const b = samples[Math.min(i + 1, samples.length - 1)];
  const t = b.arc > a.arc ? (station - a.arc) / (b.arc - a.arc) : 0;
  const tx = THREE.MathUtils.lerp(a.tx, b.tx, t);
  const tz = THREE.MathUtils.lerp(a.tz, b.tz, t);
  const len = Math.hypot(tx, tz) || 1;
  return {
    arc: station,
    x: THREE.MathUtils.lerp(a.x, b.x, t),
    z: THREE.MathUtils.lerp(a.z, b.z, t),
    tx: tx / len,
    tz: tz / len,
    widthLeft: THREE.MathUtils.lerp(a.widthLeft, b.widthLeft, t),
    widthRight: THREE.MathUtils.lerp(a.widthRight, b.widthRight, t),
    curvature: THREE.MathUtils.lerp(a.curvature, b.curvature, t),
    worldArc: THREE.MathUtils.lerp(a.worldArc, b.worldArc, t),
    tNorm: THREE.MathUtils.lerp(a.tNorm, b.tNorm, t),
  };
}

function writeKerbVertex(
  track: Track,
  positions: Float32Array,
  offset: number,
  sample: KerbSample,
  sign: number,
  style: KerbStyle,
  role: KerbVertexRole,
): void {
  const nx = -sample.tz;
  const nz = sample.tx;
  const edgeOffset = sign > 0 ? sample.widthLeft : -sample.widthRight;
  const taper = Math.sin(Math.PI * sample.tNorm);
  const taperScale = Math.max(style.endTaperFloor, taper);
  const stripWidth = (style.peakWidth + sample.curvature * style.cornerWidthBoost)
    * taperScale;
  // Lateral offset of each cross-section vertex, measured in track-edge-relative
  // metres along the outward normal. Foot of the inner ramp overlaps onto the
  // racing surface by KERB_TRACK_EDGE_OVERLAP; ramp climbs over the next
  // KERB_RAMP_RISE_LATERAL metres up to the kerb top.
  const rampTopInboard = Math.max(0, KERB_TRACK_EDGE_OVERLAP - KERB_RAMP_RISE_LATERAL);
  let lateral: number;
  let height: number;
  switch (role) {
    case KERB_VERT_BOT_IN:
      lateral = -KERB_TRACK_EDGE_OVERLAP;
      height = 0;
      break;
    case KERB_VERT_TOP_IN:
      lateral = -rampTopInboard;
      height = style.topHeight;
      break;
    case KERB_VERT_TOP_OUT:
      lateral = stripWidth;
      height = style.topHeight;
      break;
    case KERB_VERT_OUTER_BOT:
    default:
      lateral = stripWidth;
      // Keep the visual prism floor on the sampled surface. Dropping this
      // below the road creates hard shadow artifacts around the kerb edge.
      height = 0;
      break;
  }
  const lateralOffset = edgeOffset + lateral * sign;
  const wx = sample.x + nx * lateralOffset;
  const wz = sample.z + nz * lateralOffset;
  const storedX = Math.fround(wx);
  const storedZ = Math.fround(wz);
  positions[offset + 0] = storedX;
  // Use locate-from-XZ so the kerb top sits at the same banked surface height
  // the racer (and the kerb regression test) compute when sampling at this XZ.
  positions[offset + 1] = trackSurfaceHeightAtPosition(track, storedX, storedZ)
    + height
    + TRACK_SURFACE_CLEARANCE;
  positions[offset + 2] = storedZ;
}

function curvatureAt(track: Track, i: number): number {
  const n = track.centerline.length;
  const a = track.tangents[(i - 1 + n) % n];
  const b = track.tangents[(i + 1) % n];
  const dot = THREE.MathUtils.clamp(a.tx * b.tx + a.tz * b.tz, -1, 1);
  return Math.min(1, Math.acos(dot) * 5);
}

function averageCurvature(track: Track, start: number, end: number): number {
  let total = 0;
  let count = 0;
  for (let i = start; i < end; i++) {
    total += curvatureAt(track, i % track.centerline.length);
    count += 1;
  }
  return count > 0 ? total / count : 0;
}

function windowHeadingChange(track: Track, i: number): number {
  const n = track.centerline.length;
  const a = track.tangents[(i - KERB_TURN_WINDOW_POINTS + n) % n];
  const b = track.tangents[(i + KERB_TURN_WINDOW_POINTS) % n];
  const dot = THREE.MathUtils.clamp(a.tx * b.tx + a.tz * b.tz, -1, 1);
  const cross = a.tx * b.tz - a.tz * b.tx;
  return Math.atan2(cross, dot);
}

function segmentArc(track: Track, start: number, end: number): number {
  let arc = 0;
  for (let i = start + 1; i < end; i++) {
    const a = track.centerline[i - 1];
    const b = track.centerline[i];
    arc += Math.hypot(b.x - a.x, b.z - a.z);
  }
  return arc;
}

function insideKerbSign(track: Track, start: number, end: number): 1 | -1 {
  const leftArc = edgeArc(track, start, end, 1);
  const rightArc = edgeArc(track, start, end, -1);
  return leftArc < rightArc ? 1 : -1;
}

function edgeArc(track: Track, start: number, end: number, sign: 1 | -1): number {
  let arc = 0;
  let prev: { x: number; z: number } | null = null;
  for (let i = start; i < end; i++) {
    const p = track.centerline[i % track.centerline.length];
    const { tx, tz } = track.tangents[i % track.centerline.length];
    const nx = -tz;
    const nz = tx;
    const offset = sign > 0 ? p.widthLeft : -p.widthRight;
    const edge = { x: p.x + nx * offset, z: p.z + nz * offset };
    if (prev) arc += Math.hypot(edge.x - prev.x, edge.z - prev.z);
    prev = edge;
  }
  return arc;
}

function buildCenterlineGuide(track: Track): THREE.Line {
  const n = track.centerline.length;
  const pts = new Float32Array((n + 1) * 3);
  for (let i = 0; i <= n; i++) {
    const p = track.centerline[i % n];
    const y = trackSurfaceHeightAtSample(track, i % n, 0);
    pts[i * 3 + 0] = p.x;
    pts[i * 3 + 1] = y + 0.04;
    pts[i * 3 + 2] = p.z;
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(pts, 3));
  const mat = new THREE.LineBasicMaterial({ color: 0xffffff, opacity: 0.0, transparent: true });
  const line = new THREE.Line(geom, mat);
  line.visible = false;
  return line;
}

/**
 * Sub-pixel-grade luminance map centered on 0.5 — the shader reads
 * `(sample.r - 0.5) * 2 * intensity` and pushes the surface toward the
 * aggregate color. With mipmaps + 16× anisotropy the grit fades naturally
 * with distance, hiding any tile periodicity at the horizon.
 *
 * `aggregateScale` widens the grit cell — larger value = bigger flecks.
 */
function makeProceduralTexture(style: StyleConfig): THREE.DataTexture {
  const size = 512;
  const data = new Uint8Array(size * size * 4);
  const seed = 11;
  const fiberDensity = Math.max(1, 2 / Math.max(0.01, style.asphalt.aggregateScale));
  const elongation = Math.max(0.1, style.asphalt.grainElongation);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      // Two seeds avoid axial banding: one fine-grained hash, one coarser
      // fleck cell that gives the eye sparse "stones" to lock onto.
      const fiber = Math.pow(
        hashNoise(Math.floor(x / fiberDensity), Math.floor(y / (fiberDensity * elongation)), seed + 211),
        2.4,
      );
      const fleckHash = hashNoise(x, y, seed + 401);
      const fleck = fleckHash > 0.88 ? (fleckHash - 0.88) * 4 : 0;
      // Bias to 0.5 so unmodulated samples don't shift the base color, then
      // carry signed deviation in [-0.5, 0.5] for the shader to scale.
      const luminance = THREE.MathUtils.clamp(0.5 + fiber * 0.32 + fleck * 0.4 - 0.16, 0, 1);
      const byte = Math.round(luminance * 255);
      data[i + 0] = byte;
      data[i + 1] = byte;
      data[i + 2] = byte;
      data[i + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.anisotropy = 16;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

function disposeObjectChildren(group: THREE.Group): void {
  for (const child of group.children) {
    child.traverse((object) => {
      if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
        object.geometry.dispose();
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const mat of materials) mat.dispose();
      }
    });
  }
  group.clear();
}

function hashNoise(x: number, y: number, seed: number): number {
  let n = x * 374761393 + y * 668265263 + seed * 2147483647;
  n = (n ^ (n >>> 13)) * 1274126177;
  n = n ^ (n >>> 16);
  return ((n >>> 0) % 10000) / 10000;
}
